import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  PhotoIcon,
  DocumentIcon,
  FolderPlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilSquareIcon,
  XMarkIcon,
  PaperClipIcon,
  ArrowPathIcon,
  CircleStackIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import {
  Alert,
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Page,
  PageBody,
  PageHeader,
  Skeleton,
  Spinner,
  Textarea,
} from '../../components/ui';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useAuth } from '../../stores/useAuth';
import { useToasts } from '../../stores/useToasts';

const API_BASE = '/v1/microsite-generator';
const FORM_DATA_REQUEST_CONFIG = {
  // AXIOS_INSTANCE defaults to application/json; false removes that header so
  // the browser can attach the multipart boundary for FormData uploads.
  headers: { 'Content-Type': false },
};

interface MicrositeSummary {
  id: number;
  title: string;
  slug: string;
  parent_folder_id: number | null;
  public_token: string;
  source: string;
  status: string;
  share_access_mode: ShareAccessMode;
  allowed_email_domains: string[];
  allowed_email_addresses: string[];
  model_used: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface MicrositeIterationSummary {
  id: number;
  microsite_id: number;
  iteration_number: number;
  prompt: string;
  prompt_kind: string;
  base_iteration_id: number | null;
  status: string;
  model_used: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface MicrositeIterationDetail extends MicrositeIterationSummary {
  html_content: string | null;
}

interface MicrositeDetail extends MicrositeSummary {
  prompt: string | null;
  html_content: string | null;
  current_iteration_id: number | null;
  error_message: string | null;
  iterations: MicrositeIterationSummary[];
}

interface MicrositeListResponse {
  items: MicrositeSummary[];
  total: number;
}

interface BulkAnalysis {
  columns: string[];
  email_column: string | null;
  name_column: string | null;
  title_column: string | null;
  ui_prompt_column: string | null;
  row_count: number;
  sample_title: string;
  sample_participant_email: string | null;
  sample_prompt: string;
  warnings: string[];
}

interface BulkBatchItem {
  id: number;
  row_index: number;
  microsite_id: number | null;
  title: string;
  participant_email: string | null;
  participant_name: string | null;
  status: string;
  error_message: string | null;
}

interface BulkBatch {
  id: number;
  folder_id: number | null;
  source_filename: string | null;
  status: string;
  total_count: number;
  counts: Record<string, number>;
  column_mapping: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  items: BulkBatchItem[];
}

interface UploadReference {
  source_path: string;
  raw_url: string;
  reference_type: string;
  normalized_path: string | null;
  reason: string | null;
}

interface UploadAnalysis {
  ready: boolean;
  required_paths: string[];
  missing_paths: string[];
  ignored_paths?: string[];
  unsupported_references: UploadReference[];
  references: UploadReference[];
}

interface UploadPayload {
  file: File;
  assets: File[];
  ignoredPaths?: string[];
  replaceMicrositeId?: number | null;
}

interface GeneratePayload {
  prompt: string;
  attachments: File[];
}

interface StorageConfig {
  connector_name: string | null;
  connector_type: string | null;
  bucket: string | null;
  backend: string | null;
  region: string | null;
  endpoint_url: string | null;
  is_connected: boolean;
  required: boolean;
  active_backend: 'postgres' | 's3';
  mode: string;
}

interface StorageConnectionTest {
  status: string;
  s3_connected: boolean;
  message: string;
}

interface AssetFolderItem {
  id: number;
  folder_id: number;
  microsite_id: number | null;
  item_type: 'microsite' | 'external_link';
  title: string;
  slug: string;
  description: string | null;
  external_url: string | null;
  display_order: number;
  status: 'published' | 'disabled';
  created_at: string;
  updated_at: string;
}

interface AssetFolder {
  id: number;
  title: string;
  slug: string;
  parent_folder_id: number | null;
  description: string | null;
  status: 'published' | 'disabled';
  access_mode: ShareAccessMode;
  allowed_email_domains: string[];
  allowed_email_addresses: string[];
  homepage_item_id: number | null;
  published_at: string | null;
  disabled_at: string | null;
  created_at: string;
  updated_at: string;
  items: AssetFolderItem[];
  has_participant_links?: boolean;
}

interface AssetFolderListResponse {
  items: AssetFolder[];
  total: number;
}

interface AssetFolderAccessLink {
  id: number;
  folder_id: number;
  recipient_email: string | null;
  expires_at: string;
  revoked_at: string | null;
  used_at: string | null;
  access_count: number;
  url?: string | null;
}

type ShareAccessMode = 'public' | 'email_anyone' | 'email_domain' | 'email_address';

interface SharePolicyDraft {
  accessMode: ShareAccessMode;
  domains: string;
  emails: string;
  domainEntry: string;
  emailEntry: string;
}

const DEFAULT_POLICY_DRAFT: SharePolicyDraft = {
  accessMode: 'email_anyone',
  domains: '',
  emails: '',
  domainEntry: '',
  emailEntry: '',
};

const DEFAULT_BULK_POLICY_DRAFT: SharePolicyDraft = {
  ...DEFAULT_POLICY_DRAFT,
  accessMode: 'public',
};

interface CustomDomainValidationRecord {
  type: string;
  name: string;
  value: string;
  purpose: string;
}

interface CustomDomain {
  id: number;
  microsite_id: number;
  hostname: string;
  status: string;
  ownership_txt_name: string;
  ownership_txt_value: string;
  cname_target: string;
  routing_record_type: string;
  routing_record_name: string;
  routing_record_value: string;
  provider: string;
  provider_status: string | null;
  certificate_status: string | null;
  provider_validation_records: CustomDomainValidationRecord[];
  cleanup_status: string | null;
  last_checked_at: string | null;
  last_error: string | null;
}

const IN_PROGRESS_STATUSES = new Set(['queued', 'generating']);
const PREVIEW_IFRAME_SANDBOX = 'allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox';
const STORAGE_CONFIGURE_URL = '/data-connections?type=s3&new=1';
const PROMPT_ATTACHMENT_ACCEPT = [
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.gif',
  '.txt',
  '.md',
  '.html',
  '.htm',
  '.css',
  '.csv',
  '.json',
].join(',');

function suggestSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'untitled';
}

function statusBadgeVariant(status: string): 'success' | 'danger' | 'warning' | 'secondary' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (IN_PROGRESS_STATUSES.has(status)) return 'warning';
  return 'secondary';
}

function domainStatusBadgeVariant(status: string): 'success' | 'danger' | 'warning' | 'secondary' {
  if (status === 'active') return 'success';
  if (status === 'failed' || status === 'cleanup_failed') return 'danger';
  if (status === 'disabled') return 'secondary';
  return 'warning';
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function policyListToDraft(values: string[] | undefined | null) {
  return (values ?? []).join('\n');
}

function policyDraftFromFields(
  accessMode: ShareAccessMode | undefined | null,
  allowedDomains: string[] | undefined | null,
  allowedEmails: string[] | undefined | null
): SharePolicyDraft {
  return {
    accessMode: accessMode ?? 'email_anyone',
    domains: policyListToDraft(allowedDomains),
    emails: policyListToDraft(allowedEmails),
    domainEntry: '',
    emailEntry: '',
  };
}

function parsePolicyList(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[\n,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean)
    )
  );
}

function accessModeRequiresAllowlist(accessMode: ShareAccessMode) {
  return accessMode === 'email_domain' || accessMode === 'email_address';
}

function policyPayloadListsFromDraft(draft: SharePolicyDraft) {
  return {
    allowed_email_domains:
      draft.accessMode === 'email_domain' ? policyEntriesFromDraft(draft, 'domain') : [],
    allowed_email_addresses:
      draft.accessMode === 'email_address' ? policyEntriesFromDraft(draft, 'email') : [],
  };
}

function policyPayloadFromDraft(draft: SharePolicyDraft) {
  const lists = policyPayloadListsFromDraft(draft);
  return {
    access_mode: draft.accessMode,
    ...lists,
  };
}

function micrositePolicyPayloadFromDraft(draft: SharePolicyDraft) {
  const lists = policyPayloadListsFromDraft(draft);
  return {
    share_access_mode: draft.accessMode,
    ...lists,
  };
}

function samePolicyList(left: string[], right: string[] | undefined | null) {
  const normalizedRight = right ?? [];
  return left.length === normalizedRight.length && left.every((value, index) => value === normalizedRight[index]);
}

function sharePolicyDraftHasChanges(
  draft: SharePolicyDraft,
  accessMode: ShareAccessMode | undefined | null,
  allowedDomains: string[] | undefined | null,
  allowedEmails: string[] | undefined | null
) {
  const payload = policyPayloadFromDraft(draft);
  return (
    payload.access_mode !== (accessMode ?? 'email_anyone') ||
    !samePolicyList(payload.allowed_email_domains, allowedDomains) ||
    !samePolicyList(payload.allowed_email_addresses, allowedEmails)
  );
}

function accessModeLabel(accessMode: ShareAccessMode) {
  if (accessMode === 'public') return 'Public';
  if (accessMode === 'email_domain') return 'Allowed domains';
  if (accessMode === 'email_address') return 'Allowed email addresses';
  return 'Any verified email';
}

function policyEntriesFromDraft(draft: SharePolicyDraft, kind: 'domain' | 'email') {
  const listValue = kind === 'domain' ? draft.domains : draft.emails;
  const entryValue = kind === 'domain' ? draft.domainEntry : draft.emailEntry;
  return Array.from(new Set([...parsePolicyList(listValue), ...parsePolicyList(entryValue)]));
}

const ACCESS_MODE_OPTIONS: Array<{ value: ShareAccessMode; label: string }> = [
  { value: 'public', label: 'Public' },
  { value: 'email_anyone', label: 'Email gate' },
  { value: 'email_domain', label: 'Domains' },
  { value: 'email_address', label: 'Emails' },
];

function policyEntriesForMode(draft: SharePolicyDraft) {
  if (draft.accessMode === 'email_domain') return policyEntriesFromDraft(draft, 'domain');
  if (draft.accessMode === 'email_address') return policyEntriesFromDraft(draft, 'email');
  return [];
}

function accessModeNoun(accessMode: ShareAccessMode) {
  if (accessMode === 'email_domain') return 'domain';
  if (accessMode === 'email_address') return 'email address';
  return 'viewer';
}

function AccessScopeSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: ShareAccessMode;
  onChange: (value: ShareAccessMode) => void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Access scope"
      className="inline-flex max-w-full rounded-md border border-gray-200 bg-gray-50 p-0.5 dark:border-dark-border dark:bg-dark-surface-2"
    >
      {ACCESS_MODE_OPTIONS.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={[
              'h-7 rounded px-2.5 text-micro font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:px-3',
              active
                ? 'bg-white text-charcoal shadow-sm dark:bg-dark-surface dark:text-gray-100'
                : 'text-gray-500 hover:text-charcoal dark:text-gray-400 dark:hover:text-gray-100',
            ].join(' ')}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function AccessPolicyChips({
  entries,
  onRemove,
  disabled = false,
}: {
  entries: string[];
  onRemove?: (entry: string) => void;
  disabled?: boolean;
}) {
  if (entries.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap gap-1.5">
      {entries.map((entry) => (
        <span
          key={entry}
          className="inline-flex max-w-full items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 font-mono text-micro text-charcoal dark:border-dark-border dark:bg-dark-surface dark:text-gray-100"
        >
          <span className="truncate">{entry}</span>
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(entry)}
              disabled={disabled}
              className="rounded text-gray-400 hover:text-charcoal focus:outline-none focus:ring-2 focus:ring-eliza-red/30 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:text-gray-100"
              aria-label={`Remove ${entry}`}
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

function AccessAllowlistInput({
  draft,
  setDraft,
  kind,
  disabled = false,
}: {
  draft: SharePolicyDraft;
  setDraft: React.Dispatch<React.SetStateAction<SharePolicyDraft>>;
  kind: 'domain' | 'email';
  disabled?: boolean;
}) {
  const field = kind === 'domain' ? 'domains' : 'emails';
  const entryField = kind === 'domain' ? 'domainEntry' : 'emailEntry';
  const entries = parsePolicyList(draft[field]);
  const noun = kind === 'domain' ? 'domain' : 'email address';

  const addEntries = () => {
    const additions = parsePolicyList(draft[entryField]);
    if (additions.length === 0) return;
    setDraft((current) => ({
      ...current,
      [field]: policyListToDraft(Array.from(new Set([...parsePolicyList(current[field]), ...additions]))),
      [entryField]: '',
    }));
  };

  const removeEntry = (entry: string) => {
    setDraft((current) => ({
      ...current,
      [field]: policyListToDraft(parsePolicyList(current[field]).filter((item) => item !== entry)),
      [entryField]: policyListToDraft(parsePolicyList(current[entryField]).filter((item) => item !== entry)),
    }));
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <div className="flex min-w-0 flex-1 basis-72 gap-1.5 sm:max-w-md">
        <Input
          value={draft[entryField]}
          onChange={(event) => setDraft((current) => ({ ...current, [entryField]: event.target.value }))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addEntries();
            }
          }}
          placeholder={kind === 'domain' ? 'Add domain, e.g. example.com' : 'Add email, e.g. viewer@example.com'}
          aria-label={`Allowed ${noun}`}
          disabled={disabled}
          className="h-8 min-w-0 rounded-md text-small"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addEntries}
          disabled={disabled || !draft[entryField].trim()}
          className="h-8 flex-shrink-0"
        >
          Add
        </Button>
      </div>
      <AccessPolicyChips entries={entries} onRemove={removeEntry} disabled={disabled} />
    </div>
  );
}

function AccessPolicyBody({
  draft,
  setDraft,
  disabled = false,
}: {
  draft: SharePolicyDraft;
  setDraft: React.Dispatch<React.SetStateAction<SharePolicyDraft>>;
  disabled?: boolean;
}) {
  const entries = policyEntriesForMode(draft);
  const isRestricted = accessModeRequiresAllowlist(draft.accessMode);
  const kind = draft.accessMode === 'email_domain' ? 'domain' : 'email';

  if (!isRestricted) {
    return (
      <p className="text-micro text-gray-500 dark:text-gray-400">
        {draft.accessMode === 'public'
          ? 'No email verification required.'
          : 'Every viewer verifies by email.'}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <AccessAllowlistInput draft={draft} setDraft={setDraft} kind={kind} disabled={disabled} />
      {entries.length === 0 && (
        <p className="text-micro text-gray-500 dark:text-gray-400">
          Add at least one {accessModeNoun(draft.accessMode)} before saving.
        </p>
      )}
    </div>
  );
}

function AccessPolicySummary({ draft, label = 'Allowed viewers' }: { draft: SharePolicyDraft; label?: string }) {
  const entries = policyEntriesForMode(draft);
  const emptyRestrictedList = accessModeRequiresAllowlist(draft.accessMode) && entries.length === 0;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-small font-semibold text-charcoal dark:text-gray-100">{label}</p>
        <Badge variant="secondary">{accessModeLabel(draft.accessMode)}</Badge>
      </div>
      {!accessModeRequiresAllowlist(draft.accessMode) ? (
        <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
          {draft.accessMode === 'public'
            ? 'No email verification required.'
            : 'Every viewer verifies by email.'}
        </p>
      ) : emptyRestrictedList ? (
        <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
          Add at least one {accessModeNoun(draft.accessMode)} before saving.
        </p>
      ) : (
        <div className="mt-1.5">
          <AccessPolicyChips entries={entries} />
        </div>
      )}
    </div>
  );
}

function FolderAccessControlPanel({
  draft,
  setDraft,
  onSave,
  isSaving,
  hasChanges,
}: {
  draft: SharePolicyDraft;
  setDraft: React.Dispatch<React.SetStateAction<SharePolicyDraft>>;
  onSave: () => void;
  isSaving: boolean;
  hasChanges: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-small font-semibold text-charcoal dark:text-gray-100">Allowed viewers</p>
        <Badge variant="secondary">{accessModeLabel(draft.accessMode)}</Badge>
      </div>
      <div className="mt-2 flex flex-wrap items-start gap-2">
        <AccessScopeSelector
          value={draft.accessMode}
          onChange={(accessMode) => setDraft((current) => ({ ...current, accessMode }))}
          disabled={isSaving}
        />
        <div className="min-w-0 flex-1 basis-72">
          <AccessPolicyBody draft={draft} setDraft={setDraft} disabled={isSaving} />
        </div>
      </div>
      <div className="mt-3 flex justify-end border-t border-gray-200 pt-3 dark:border-dark-border">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSave}
          disabled={isSaving || !hasChanges}
          className="h-8"
          aria-label="Save access"
        >
          {isSaving && <Spinner size="sm" className="mr-1.5" />}
          {isSaving ? 'Saving' : 'Save access'}
        </Button>
      </div>
    </div>
  );
}

function MicrositeAccessControlPanel({
  draft,
  setDraft,
  onSave,
  isSaving,
  hasChanges,
}: {
  draft: SharePolicyDraft;
  setDraft: React.Dispatch<React.SetStateAction<SharePolicyDraft>>;
  onSave: () => void;
  isSaving: boolean;
  hasChanges: boolean;
}) {
  const entries = policyEntriesForMode(draft);
  const isRestricted = accessModeRequiresAllowlist(draft.accessMode);

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2/70">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="flex min-h-8 flex-shrink-0 items-center">
          <p className="text-micro font-semibold uppercase text-gray-500 dark:text-gray-400">Share access</p>
        </div>
        <AccessScopeSelector
          value={draft.accessMode}
          onChange={(accessMode) => setDraft((current) => ({ ...current, accessMode }))}
          disabled={isSaving}
        />
        <div className="min-w-0 flex-1 basis-[28rem]">
          {isRestricted ? (
            <div className="flex min-w-0 flex-col gap-1">
              <AccessAllowlistInput
                draft={draft}
                setDraft={setDraft}
                kind={draft.accessMode === 'email_domain' ? 'domain' : 'email'}
                disabled={isSaving}
              />
              {entries.length === 0 && (
                <p className="text-micro text-gray-500 dark:text-gray-400">
                  Add at least one {accessModeNoun(draft.accessMode)} before saving.
                </p>
              )}
            </div>
          ) : (
            <p className="text-micro text-gray-500 dark:text-gray-400">
              {draft.accessMode === 'public'
                ? 'No email verification required.'
                : 'Every viewer verifies by email.'}
            </p>
          )}
        </div>
      </div>
      <div className="mt-2 flex justify-end border-t border-gray-200 pt-2 dark:border-dark-border">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSave}
          disabled={isSaving || !hasChanges}
          className="h-8 flex-shrink-0"
          aria-label="Save access"
        >
          {isSaving && <Spinner size="sm" className="mr-1.5" />}
          {isSaving ? 'Saving' : 'Save access'}
        </Button>
      </div>
    </div>
  );
}

function InheritedAccessNotice({
  draft,
  sourcePath,
}: {
  draft: SharePolicyDraft;
  sourcePath: string;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-small font-semibold text-charcoal dark:text-gray-100">Inherited access</p>
        <Badge variant="secondary">{accessModeLabel(draft.accessMode)}</Badge>
        <span className="text-micro text-gray-500 dark:text-gray-400">from /{sourcePath}</span>
      </div>
      {!accessModeRequiresAllowlist(draft.accessMode) ? (
        <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
          {draft.accessMode === 'public'
            ? 'No email verification required.'
            : 'Every viewer verifies by email.'}
        </p>
      ) : policyEntriesForMode(draft).length === 0 ? (
        <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
          Add at least one {accessModeNoun(draft.accessMode)} on the parent folder before saving.
        </p>
      ) : (
        <div className="mt-1.5">
          <AccessPolicyChips entries={policyEntriesForMode(draft)} />
        </div>
      )}
    </div>
  );
}

function rawBundlePathForFile(file: File) {
  const rawPath =
    (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  return rawPath.replace(/\\/g, '/').replace(/^\/+/, '');
}

function inferBundleRoot(rawPaths: string[], requiredPaths: string[]) {
  const sortedRequiredPaths = [...requiredPaths].filter(Boolean).sort((a, b) => b.length - a.length);
  for (const rawPath of rawPaths) {
    for (const requiredPath of sortedRequiredPaths) {
      if (rawPath === requiredPath) {
        return '';
      }
      if (rawPath.endsWith(`/${requiredPath}`)) {
        const root = rawPath.slice(0, rawPath.length - requiredPath.length);
        return root.replace(/\/+$/, '/');
      }
    }
  }
  return '';
}

function bundlePathForFile(file: File, requiredPaths: string[] = [], bundleRoot = '') {
  const rawPath = rawBundlePathForFile(file);
  // Folder uploads include a browser-supplied root directory; strip it before recursive analysis.
  const normalized =
    bundleRoot && rawPath.startsWith(bundleRoot)
      ? rawPath.slice(bundleRoot.length)
      : rawPath;
  const matchedPath = [...requiredPaths]
    .sort((a, b) => b.length - a.length)
    .find((path) => normalized === path || normalized.endsWith(`/${path}`));
  return matchedPath || normalized;
}

function uniqueSortedPaths(paths: string[]) {
  return Array.from(new Set(paths.filter(Boolean))).sort();
}

function buildUploadForm(
  { file, assets, ignoredPaths = [] }: UploadPayload,
  requiredPaths: string[] = []
) {
  const form = new FormData();
  const bundleRoot = inferBundleRoot(assets.map(rawBundlePathForFile), requiredPaths);
  form.append('file', file);
  assets.forEach((asset) => {
    form.append('assets', asset);
    form.append('asset_paths', bundlePathForFile(asset, requiredPaths, bundleRoot));
  });
  uniqueSortedPaths(ignoredPaths).forEach((path) => {
    form.append('ignored_paths', path);
  });
  return form;
}

function buildGenerateForm({ prompt, attachments }: GeneratePayload) {
  const form = new FormData();
  form.append('prompt', prompt);
  attachments.forEach((attachment) => form.append('attachments', attachment));
  return form;
}

function fetchStorageConfig() {
  return AXIOS_INSTANCE.get<StorageConfig>(`${API_BASE}/config/storage`).then((r) => r.data);
}

function promptAttachmentKey(file: File, index: number) {
  return `${file.name}-${file.size}-${file.lastModified}-${index}`;
}

function analysisFromError(error: unknown): UploadAnalysis | null {
  const response = (error as {
    response?: { status?: number; data?: { detail?: unknown } };
  }).response;
  if (
    response?.status !== 422 ||
    typeof response.data?.detail !== 'object' ||
    Array.isArray(response.data.detail)
  ) {
    return null;
  }
  return response.data.detail as UploadAnalysis;
}

function requestErrorMessage(error: unknown, fallback: string) {
  const detail = (error as {
    response?: { data?: { detail?: unknown } };
  }).response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return 'The request was not sent as a valid file form. Please try again.';
  }
  return fallback;
}

export default function MicrositeGeneratorPage() {
  const queryClient = useQueryClient();
  const { hasAnyPermission } = useAuth();
  const { push: addToast } = useToasts();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const refinementTextareaRef = useRef<HTMLTextAreaElement>(null);
  const promptAttachmentsInputRef = useRef<HTMLInputElement>(null);
  const supportingFilesInputRef = useRef<HTMLInputElement>(null);
  const supportingFolderInputRef = useRef<HTMLInputElement>(null);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedIterationId, setSelectedIterationId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState('');
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [promptAttachments, setPromptAttachments] = useState<File[]>([]);
  const [promptAttachmentPreviewUrls, setPromptAttachmentPreviewUrls] = useState<Record<string, string>>({});
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [pendingUploadTargetId, setPendingUploadTargetId] = useState<number | null>(null);
  const [supportingFiles, setSupportingFiles] = useState<File[]>([]);
  const [ignoredMissingPaths, setIgnoredMissingPaths] = useState<string[]>([]);
  const [uploadAnalysis, setUploadAnalysis] = useState<UploadAnalysis | null>(null);
  const [isAnalyzingUpload, setIsAnalyzingUpload] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [slugDraft, setSlugDraft] = useState('');
  const [domainHostDraft, setDomainHostDraft] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [activeArea, setActiveArea] = useState<'microsites' | 'folders'>('microsites');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [folderDraft, setFolderDraft] = useState({ title: '', slug: '', description: '', parentFolderId: '' });
  const [folderEditDraft, setFolderEditDraft] = useState({
    title: '',
    slug: '',
    description: '',
    parentFolderId: '',
  });
  const [folderPolicyDraft, setFolderPolicyDraft] = useState<SharePolicyDraft>(DEFAULT_POLICY_DRAFT);
  const [micrositePolicyDraft, setMicrositePolicyDraft] = useState<SharePolicyDraft>(DEFAULT_POLICY_DRAFT);
  const [folderItemDraft, setFolderItemDraft] = useState({
    title: '',
    slug: '',
    description: '',
    micrositeId: '',
    externalUrl: '',
    itemType: 'microsite' as 'microsite' | 'external_link',
  });
  const [folderAccessEmail, setFolderAccessEmail] = useState('');
  const canWrite = hasAnyPermission(['microsite_generator:write', 'platform:admin']);

  // --- Bulk upload (CSV/XLSX) state ---
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkFolderName, setBulkFolderName] = useState('');
  const [bulkPolicyDraft, setBulkPolicyDraft] = useState<SharePolicyDraft>(DEFAULT_BULK_POLICY_DRAFT);
  const [bulkAnalysis, setBulkAnalysis] = useState<BulkAnalysis | null>(null);
  const [bulkAnalyzing, setBulkAnalyzing] = useState(false);
  const [bulkAnalyzeError, setBulkAnalyzeError] = useState<string | null>(null);
  const [bulkBatchId, setBulkBatchId] = useState<number | null>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const clearBulkInputs = () => {
    setBulkFile(null);
    setBulkFolderName('');
    setBulkPolicyDraft(DEFAULT_BULK_POLICY_DRAFT);
    setBulkAnalysis(null);
    setBulkAnalyzing(false);
    setBulkAnalyzeError(null);
    if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
  };

  const resetBulkState = () => {
    clearBulkInputs();
    setBulkBatchId(null);
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    // Intentionally keep bulkBatchId: an in-flight batch keeps polling in the background (the query
    // is enabled on bulkBatchId alone) so the terminal-status effect still invalidates the list and
    // folder queries when generation finishes, even if the modal was closed first. The "Bulk
    // Upload" button calls resetBulkState() for a fresh start.
    clearBulkInputs();
  };

  const handleBulkFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setBulkAnalysis(null);
    setBulkAnalyzeError(null);
    setBulkFile(file);
    if (!file) return;
    setBulkAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await AXIOS_INSTANCE.post<BulkAnalysis>(
        `${API_BASE}/microsites/bulk/analyze`,
        formData,
        FORM_DATA_REQUEST_CONFIG,
      ).then((r) => r.data);
      setBulkAnalysis(data);
    } catch (err: any) {
      setBulkAnalyzeError(
        err?.response?.data?.detail || err?.message || 'Failed to analyze file.',
      );
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const bulkPolicyInvalid =
    accessModeRequiresAllowlist(bulkPolicyDraft.accessMode) &&
    policyEntriesForMode(bulkPolicyDraft).length === 0;

  const bulkCreateMutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('file', bulkFile as File);
      if (bulkFolderName.trim()) formData.append('folder_name', bulkFolderName.trim());
      const policyPayload = policyPayloadFromDraft(bulkPolicyDraft);
      formData.append('access_mode', policyPayload.access_mode);
      policyPayload.allowed_email_domains.forEach((domain) => {
        formData.append('allowed_email_domains', domain);
      });
      policyPayload.allowed_email_addresses.forEach((email) => {
        formData.append('allowed_email_addresses', email);
      });
      return AXIOS_INSTANCE.post<BulkBatch>(
        `${API_BASE}/microsites/bulk`,
        formData,
        FORM_DATA_REQUEST_CONFIG,
      ).then((r) => r.data);
    },
    onSuccess: (batch) => {
      setBulkBatchId(batch.id);
      addToast({ kind: 'success', message: `Generating ${batch.total_count} microsites…` });
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
    },
    onError: (err: any) => {
      addToast({
        kind: 'error',
        message:
          err?.response?.data?.detail || err?.message || 'Bulk upload failed. Please try again.',
      });
    },
  });

  const bulkBatchQuery = useQuery<BulkBatch>({
    queryKey: ['microsite-bulk-batch', bulkBatchId],
    queryFn: () =>
      AXIOS_INSTANCE.get(`${API_BASE}/microsites/bulk/batches/${bulkBatchId}`).then(
        (r) => r.data,
      ),
    // Enabled on bulkBatchId alone (not modal state) so a batch started then closed keeps polling
    // to completion and the terminal-status effect can refresh the list/folder queries.
    enabled: bulkBatchId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && ['completed', 'completed_with_errors', 'failed'].includes(status)
        ? false
        : 3000;
    },
  });

  useEffect(() => {
    const status = bulkBatchQuery.data?.status;
    if (status && ['completed', 'completed_with_errors', 'failed'].includes(status)) {
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
    }
  }, [bulkBatchQuery.data?.status, queryClient]);

  useEffect(() => {
    const urls: Record<string, string> = {};
    promptAttachments.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        urls[promptAttachmentKey(file, index)] = URL.createObjectURL(file);
      }
    });
    setPromptAttachmentPreviewUrls(urls);
    return () => {
      Object.keys(urls).forEach((key) => URL.revokeObjectURL(urls[key]));
    };
  }, [promptAttachments]);

  useEffect(() => {
    const textarea = promptTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 420)}px`;
  }, [prompt]);

  useEffect(() => {
    const textarea = refinementTextareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 220)}px`;
  }, [refinementPrompt]);

  const listQuery = useQuery<MicrositeListResponse>({
    queryKey: ['microsite-list'],
    // Fetch a large page so folders with many bulk-generated microsites (e.g. 50+) show all of
    // their contents. The folder "contents" view filters this list client-side, so a small page
    // size would silently truncate large folders. page_size is passed via axios params (not the
    // URL string) so the request path stays `/microsites`.
    queryFn: () =>
      AXIOS_INSTANCE.get(`${API_BASE}/microsites`, { params: { page_size: 1000 } }).then(
        (r) => r.data,
      ),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((m) => IN_PROGRESS_STATUSES.has(m.status)) ? 3000 : false;
    },
  });

  const folderListQuery = useQuery<AssetFolderListResponse>({
    queryKey: ['microsite-asset-folders'],
    queryFn: () => AXIOS_INSTANCE.get(`${API_BASE}/asset-folders`).then((r) => r.data),
  });

  const folders = folderListQuery.data?.items ?? [];
  const selectedFolder = folders.find((folder) => folder.id === selectedFolderId) ?? null;
  const folderPolicyHasChanges = selectedFolder
    ? sharePolicyDraftHasChanges(
        folderPolicyDraft,
        selectedFolder.access_mode,
        selectedFolder.allowed_email_domains,
        selectedFolder.allowed_email_addresses,
      )
    : false;
  const folderById = useMemo(() => new Map(folders.map((folder) => [folder.id, folder])), [folders]);

  const folderPath = (folder: AssetFolder | null): string => {
    if (!folder) return '';
    const parts = [folder.slug];
    let parentId = folder.parent_folder_id;
    const seen = new Set<number>([folder.id]);
    while (parentId) {
      const parent = folderById.get(parentId);
      if (!parent || seen.has(parent.id)) break;
      parts.unshift(parent.slug);
      seen.add(parent.id);
      parentId = parent.parent_folder_id;
    }
    return parts.join('/');
  };

  const micrositeSharePath = (microsite: MicrositeSummary | MicrositeDetail): string => {
    if (!microsite.parent_folder_id) return microsite.slug;
    const parent = folderById.get(microsite.parent_folder_id);
    const parentPath = folderPath(parent ?? null);
    return parentPath ? `${parentPath}/${microsite.slug}` : microsite.slug;
  };

  const folderAccessLinksQuery = useQuery<{ items: AssetFolderAccessLink[] }>({
    queryKey: ['microsite-asset-folder-access-links', selectedFolderId],
    queryFn: () =>
      AXIOS_INSTANCE.get(`${API_BASE}/asset-folders/${selectedFolderId}/access-links`).then((r) => r.data),
    enabled: selectedFolderId !== null,
  });

  useEffect(() => {
    if (!selectedFolder) {
      setFolderEditDraft({ title: '', slug: '', description: '', parentFolderId: '' });
      setFolderPolicyDraft(DEFAULT_POLICY_DRAFT);
      return;
    }
    setFolderEditDraft({
      title: selectedFolder.title,
      slug: selectedFolder.slug,
      description: selectedFolder.description ?? '',
      parentFolderId: selectedFolder.parent_folder_id ? String(selectedFolder.parent_folder_id) : '',
    });
    setFolderPolicyDraft(
      policyDraftFromFields(
        selectedFolder.access_mode,
        selectedFolder.allowed_email_domains,
        selectedFolder.allowed_email_addresses,
      )
    );
  }, [
    selectedFolder?.id,
    selectedFolder?.title,
    selectedFolder?.slug,
    selectedFolder?.description,
    selectedFolder?.parent_folder_id,
    selectedFolder?.access_mode,
    selectedFolder?.allowed_email_domains,
    selectedFolder?.allowed_email_addresses,
  ]);

  const detailQuery = useQuery<MicrositeDetail>({
    queryKey: ['microsite-detail', selectedId],
    queryFn: () =>
      AXIOS_INSTANCE.get(`${API_BASE}/microsites/${selectedId}`).then((r) => r.data),
    enabled: selectedId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && IN_PROGRESS_STATUSES.has(status) ? 3000 : false;
    },
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    setSelectedIterationId((current) => {
      const iterations = detailQuery.data.iterations ?? [];
      if (current && iterations.some((iteration) => iteration.id === current)) {
        return current;
      }
      return detailQuery.data.current_iteration_id ?? null;
    });
  }, [detailQuery.data]);

  useEffect(() => {
    if (!detailQuery.data) {
      setMicrositePolicyDraft(DEFAULT_POLICY_DRAFT);
      return;
    }
    setMicrositePolicyDraft(
      policyDraftFromFields(
        detailQuery.data.share_access_mode,
        detailQuery.data.allowed_email_domains,
        detailQuery.data.allowed_email_addresses,
      )
    );
  }, [
    detailQuery.data?.id,
    detailQuery.data?.share_access_mode,
    detailQuery.data?.allowed_email_domains,
    detailQuery.data?.allowed_email_addresses,
  ]);

  const selectedIterationQuery = useQuery<MicrositeIterationDetail>({
    queryKey: ['microsite-iteration', selectedId, selectedIterationId],
    queryFn: () =>
      AXIOS_INSTANCE.get(
        `${API_BASE}/microsites/${selectedId}/iterations/${selectedIterationId}`
      ).then((r) => r.data),
    enabled:
      selectedId !== null &&
      selectedIterationId !== null &&
      selectedIterationId !== detailQuery.data?.current_iteration_id,
  });

  const storageConfigQuery = useQuery<StorageConfig>({
    queryKey: ['microsite-storage-config'],
    queryFn: fetchStorageConfig,
    enabled: canWrite,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const customDomainQuery = useQuery<CustomDomain | null>({
    queryKey: ['microsite-custom-domain', selectedId],
    queryFn: () =>
      AXIOS_INSTANCE.get(`${API_BASE}/microsites/${selectedId}/custom-domain`).then((r) => {
        const data = r.data as CustomDomain | null;
        return data && typeof data.hostname === 'string' ? data : null;
      }),
    enabled: selectedId !== null,
    retry: false,
  });

  const testStorageMutation = useMutation({
    mutationFn: () =>
      AXIOS_INSTANCE.post<StorageConnectionTest>(`${API_BASE}/config/storage/test-connection`).then((r) => r.data),
    onSuccess: (data) => {
      addToast({
        kind: data.s3_connected ? 'success' : 'warning',
        message: data.message || (data.s3_connected ? 'S3 storage is healthy' : 'S3 storage test failed'),
      });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to test S3 storage') });
    },
  });

  const generateMutation = useMutation({
    mutationFn: ({ prompt: promptText, attachments }: GeneratePayload) => {
      if (attachments.length > 0) {
        return AXIOS_INSTANCE.post<MicrositeDetail>(
          `${API_BASE}/microsites/generate`,
          buildGenerateForm({ prompt: promptText, attachments }),
          FORM_DATA_REQUEST_CONFIG
        ).then((r) => r.data);
      }
      return AXIOS_INSTANCE.post<MicrositeDetail>(
        `${API_BASE}/microsites`,
        { prompt: promptText }
      ).then((r) => r.data);
    },
    onSuccess: (data) => {
      setPrompt('');
      setPromptAttachments([]);
      setSelectedId(data.id);
      setSelectedIterationId(data.current_iteration_id);
      setIsEditingTitle(false);
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({ kind: 'success', message: 'Microsite generation started' });
    },
    onError: (error) => {
      addToast({
        kind: 'error',
        message: requestErrorMessage(error, 'Failed to start microsite generation'),
      });
    },
  });

  const refineMutation = useMutation({
    mutationFn: ({ id, prompt: promptText }: { id: number; prompt: string }) =>
      AXIOS_INSTANCE.post<MicrositeDetail>(`${API_BASE}/microsites/${id}/iterations`, {
        prompt: promptText,
      }).then((r) => r.data),
    onSuccess: (data) => {
      setRefinementPrompt('');
      queryClient.setQueryData<MicrositeDetail>(['microsite-detail', data.id], data);
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({ kind: 'success', message: 'Microsite refinement started' });
    },
    onError: (error) => {
      addToast({
        kind: 'error',
        message: requestErrorMessage(error, 'Failed to start microsite refinement'),
      });
    },
  });

  const activateIterationMutation = useMutation({
    mutationFn: ({ id, iterationId }: { id: number; iterationId: number }) =>
      AXIOS_INSTANCE.post<MicrositeDetail>(
        `${API_BASE}/microsites/${id}/iterations/${iterationId}/activate`
      ).then((r) => r.data),
    onSuccess: (data) => {
      setSelectedIterationId(data.current_iteration_id);
      queryClient.setQueryData<MicrositeDetail>(['microsite-detail', data.id], data);
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({ kind: 'success', message: 'Microsite version restored' });
    },
    onError: (error) => {
      addToast({
        kind: 'error',
        message: requestErrorMessage(error, 'Failed to restore microsite version'),
      });
    },
  });

  const downloadHtmlMutation = useMutation({
    mutationFn: async (microsite: MicrositeDetail) => {
      const response = await AXIOS_INSTANCE.get(`${API_BASE}/microsites/${microsite.id}/download`, {
        responseType: 'blob',
      });
      return { microsite, blob: response.data as Blob };
    },
    onSuccess: ({ microsite, blob }) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${microsite.slug || suggestSlug(microsite.title)}.html`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      addToast({ kind: 'success', message: 'HTML downloaded' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to download HTML') });
    },
  });

  const downloadLinksCsvMutation = useMutation({
    mutationFn: async ({ url }: { url: string; filename: string }) => {
      const response = await AXIOS_INSTANCE.get(url, { responseType: 'blob' });
      return response.data as Blob;
    },
    onSuccess: (blob, { filename }) => {
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      addToast({ kind: 'success', message: 'Participant links CSV downloaded' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to download CSV') });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (payload: UploadPayload) => {
      const form = buildUploadForm(
        {
          ...payload,
          ignoredPaths: payload.ignoredPaths ?? ignoredMissingPaths,
        },
        uploadAnalysis?.required_paths
      );
      const url = payload.replaceMicrositeId
        ? `${API_BASE}/microsites/${payload.replaceMicrositeId}/upload`
        : `${API_BASE}/microsites/upload`;
      const r = await AXIOS_INSTANCE.post<MicrositeDetail>(
        url,
        form,
        FORM_DATA_REQUEST_CONFIG
      );
      return r.data;
    },
    onSuccess: (data, variables) => {
      setSelectedId(data.id);
      setSelectedIterationId(data.current_iteration_id);
      setIsEditingTitle(false);
      resetUploadState();
      queryClient.removeQueries({ queryKey: ['microsite-detail', data.id], exact: true });
      queryClient.invalidateQueries({ queryKey: ['microsite-detail', data.id] });
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({
        kind: 'success',
        message: variables.replaceMicrositeId
          ? 'HTML version uploaded successfully'
          : 'HTML file uploaded successfully',
      });
    },
    onError: (error) => {
      const analysis = analysisFromError(error);
      if (analysis) {
        setUploadAnalysis(analysis);
        setIgnoredMissingPaths(analysis.ignored_paths ?? ignoredMissingPaths);
        addToast({ kind: 'warning', message: 'Add the missing files to finish upload' });
        return;
      }
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to upload HTML bundle') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => AXIOS_INSTANCE.delete(`${API_BASE}/microsites/${id}`),
    onSuccess: () => {
      setSelectedId(null);
      setSelectedIterationId(null);
      setIsEditingTitle(false);
      setDeleteConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({ kind: 'success', message: 'Microsite deleted' });
    },
    onError: () => {
      addToast({ kind: 'error', message: 'Failed to delete microsite' });
    },
  });

  const updateMicrositeMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Pick<MicrositeDetail, 'title' | 'slug' | 'parent_folder_id' | 'share_access_mode' | 'allowed_email_domains' | 'allowed_email_addresses'>> }) =>
      AXIOS_INSTANCE.patch<MicrositeDetail>(`${API_BASE}/microsites/${id}`, payload).then((r) => r.data),
    onSuccess: (data) => {
      setTitleDraft(data.title);
      setSlugDraft(data.slug ?? suggestSlug(data.title));
      setIsEditingTitle(false);
      queryClient.setQueryData<MicrositeDetail>(['microsite-detail', data.id], data);
      queryClient.setQueryData<MicrositeListResponse>(['microsite-list'], (previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          items: previous.items.map((item) =>
            item.id === data.id
              ? {
                  ...item,
                  title: data.title,
                  slug: data.slug ?? suggestSlug(data.title),
                  parent_folder_id: data.parent_folder_id ?? null,
                  share_access_mode: data.share_access_mode,
                  allowed_email_domains: data.allowed_email_domains,
                  allowed_email_addresses: data.allowed_email_addresses,
                  updated_at: data.updated_at,
                }
              : item
          ),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
      addToast({ kind: 'success', message: 'Microsite updated' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to update microsite') });
    },
  });

  const createCustomDomainMutation = useMutation({
    mutationFn: ({ id, hostname }: { id: number; hostname: string }) =>
      AXIOS_INSTANCE.post<CustomDomain>(`${API_BASE}/microsites/${id}/custom-domain`, {
        hostname,
      }).then((r) => r.data),
    onSuccess: (data) => {
      setDomainHostDraft('');
      queryClient.setQueryData<CustomDomain | null>(['microsite-custom-domain', data.microsite_id], data);
      addToast({ kind: 'success', message: 'Custom domain added' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to add custom domain') });
    },
  });

  const checkCustomDomainMutation = useMutation({
    mutationFn: (id: number) =>
      AXIOS_INSTANCE.post<CustomDomain>(`${API_BASE}/microsites/${id}/custom-domain/check`).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData<CustomDomain | null>(['microsite-custom-domain', data.microsite_id], data);
      addToast({ kind: data.status === 'active' ? 'success' : 'info', message: 'Custom domain status checked' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to check custom domain') });
    },
  });

  const disableCustomDomainMutation = useMutation({
    mutationFn: (id: number) =>
      AXIOS_INSTANCE.delete<CustomDomain>(`${API_BASE}/microsites/${id}/custom-domain`).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData<CustomDomain | null>(['microsite-custom-domain', data.microsite_id], data);
      addToast({ kind: 'success', message: 'Custom domain disabled' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to disable custom domain') });
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: () =>
      AXIOS_INSTANCE.post<AssetFolder>(`${API_BASE}/asset-folders`, {
        title: folderDraft.title,
        slug: folderDraft.slug || undefined,
        description: folderDraft.description || undefined,
        parent_folder_id: folderDraft.parentFolderId ? Number(folderDraft.parentFolderId) : undefined,
      }).then((r) => r.data),
    onSuccess: (folder) => {
      setFolderDraft({ title: '', slug: '', description: '', parentFolderId: '' });
      setSelectedFolderId(folder.id);
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
      addToast({ kind: 'success', message: 'Folder created' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to create folder') });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: ({ folderId, payload }: { folderId: number; payload: Partial<AssetFolder> }) =>
      AXIOS_INSTANCE.patch<AssetFolder>(`${API_BASE}/asset-folders/${folderId}`, payload).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
      addToast({ kind: 'success', message: 'Folder updated' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to update folder') });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: number) => AXIOS_INSTANCE.delete(`${API_BASE}/asset-folders/${folderId}`),
    onSuccess: () => {
      setSelectedFolderId(null);
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({ kind: 'success', message: 'Folder deleted' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to delete folder') });
    },
  });

  const createFolderItemMutation = useMutation({
    mutationFn: () =>
      AXIOS_INSTANCE.post<AssetFolderItem>(`${API_BASE}/asset-folders/${selectedFolderId}/items`, {
        item_type: folderItemDraft.itemType,
        title: folderItemDraft.itemType === 'external_link' ? folderItemDraft.title : undefined,
        slug: folderItemDraft.itemType === 'external_link' ? folderItemDraft.slug || undefined : undefined,
        description: folderItemDraft.description || undefined,
        microsite_id: folderItemDraft.itemType === 'microsite' ? Number(folderItemDraft.micrositeId) : undefined,
        external_url: folderItemDraft.itemType === 'external_link' ? folderItemDraft.externalUrl : undefined,
      }).then((r) => r.data),
    onSuccess: () => {
      setFolderItemDraft({
        title: '',
        slug: '',
        description: '',
        micrositeId: '',
        externalUrl: '',
        itemType: 'microsite',
      });
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
      queryClient.invalidateQueries({ queryKey: ['microsite-list'] });
      addToast({ kind: 'success', message: 'Item added to folder' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to add folder item') });
    },
  });

  const disableFolderItemMutation = useMutation({
    mutationFn: ({ folderId, itemId }: { folderId: number; itemId: number }) =>
      AXIOS_INSTANCE.delete(`${API_BASE}/asset-folders/${folderId}/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
      addToast({ kind: 'success', message: 'Folder item disabled' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to disable folder item') });
    },
  });

  const reorderFolderItemsMutation = useMutation({
    mutationFn: ({ folderId, itemIds }: { folderId: number; itemIds: number[] }) =>
      AXIOS_INSTANCE.post(`${API_BASE}/asset-folders/${folderId}/items/reorder`, { item_ids: itemIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folders'] });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to reorder folder items') });
    },
  });

  const createFolderAccessLinkMutation = useMutation({
    mutationFn: () =>
      AXIOS_INSTANCE.post<AssetFolderAccessLink>(
        `${API_BASE}/asset-folders/${selectedFolderId}/access-links`,
        { recipient_email: folderAccessEmail || undefined }
      ).then((r) => r.data),
    onSuccess: (link) => {
      setFolderAccessEmail('');
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folder-access-links', selectedFolderId] });
      if (link.url) {
        navigator.clipboard.writeText(link.url);
      }
      addToast({ kind: 'success', message: link.url ? 'Access link created and copied' : 'Access link created' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to create folder access link') });
    },
  });

  const revokeFolderAccessLinkMutation = useMutation({
    mutationFn: ({ folderId, linkId }: { folderId: number; linkId: number }) =>
      AXIOS_INSTANCE.delete(`${API_BASE}/asset-folders/${folderId}/access-links/${linkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['microsite-asset-folder-access-links', selectedFolderId] });
      addToast({ kind: 'success', message: 'Folder access link revoked' });
    },
    onError: (error) => {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Failed to revoke folder access link') });
    },
  });

  const storageConnected = Boolean(storageConfigQuery.data?.is_connected);
  const storageRequiredButMissing =
    canWrite && Boolean(storageConfigQuery.data?.required) && !storageConnected;
  const storageAllowsWrites = !storageRequiredButMissing;
  const storageBusy =
    storageConfigQuery.isLoading ||
    testStorageMutation.isPending;

  const requireStorageAvailable = () => {
    if (storageAllowsWrites) return true;
    addToast({ kind: 'error', message: 'Configure tenant object storage before creating microsites' });
    return false;
  };

  const refreshStorageConfig = () => {
    queryClient.invalidateQueries({ queryKey: ['microsite-storage-config'] });
  };

  const handleGenerate = () => {
    if (!requireStorageAvailable()) return;
    const trimmed = prompt.trim();
    if (!trimmed) return;
    generateMutation.mutate({ prompt: trimmed, attachments: promptAttachments });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleGenerate();
    }
  };

  const handleRefine = () => {
    if (selectedId === null) return;
    const trimmed = refinementPrompt.trim();
    if (!trimmed) return;
    refineMutation.mutate({ id: selectedId, prompt: trimmed });
  };

  const handleRefineKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleRefine();
    }
  };

  const resetUploadState = () => {
    setPendingUploadFile(null);
    setPendingUploadTargetId(null);
    setSupportingFiles([]);
    setIgnoredMissingPaths([]);
    setUploadAnalysis(null);
  };

  const analyzeUpload = async (
    file: File,
    assets: File[],
    ignoredPaths = ignoredMissingPaths
  ) => {
    setIsAnalyzingUpload(true);
    try {
      const r = await AXIOS_INSTANCE.post<UploadAnalysis>(
        `${API_BASE}/microsites/upload/analyze`,
        buildUploadForm(
          { file, assets, ignoredPaths },
          uploadAnalysis?.required_paths
        ),
        FORM_DATA_REQUEST_CONFIG
      );
      setUploadAnalysis(r.data);
      setIgnoredMissingPaths(r.data.ignored_paths ?? uniqueSortedPaths(ignoredPaths));
      return r.data;
    } catch (error) {
      addToast({ kind: 'error', message: requestErrorMessage(error, 'Could not inspect the HTML bundle') });
      return null;
    } finally {
      setIsAnalyzingUpload(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!requireStorageAvailable()) {
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    if (file) {
      const replaceMicrositeId = pendingUploadTargetId;
      setPendingUploadFile(file);
      setSupportingFiles([]);
      setIgnoredMissingPaths([]);
      const analysis = await analyzeUpload(file, [], []);
      if (analysis?.ready) {
        uploadMutation.mutate({ file, assets: [], ignoredPaths: [], replaceMicrositeId });
      } else if (analysis) {
        addToast({ kind: 'warning', message: 'Add supporting files to finish upload' });
      }
    }
    e.target.value = '';
  };

  const handlePromptAttachmentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      setPromptAttachments((current) => [...current, ...files].slice(0, 5));
    }
    e.target.value = '';
  };

  const removePromptAttachment = (indexToRemove: number) => {
    setPromptAttachments((current) => current.filter((_file, index) => index !== indexToRemove));
  };

  const handleSupportingFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!pendingUploadFile || files.length === 0) {
      e.target.value = '';
      return;
    }

    const requiredPaths = uploadAnalysis?.required_paths ?? [];
    const allFiles = [...supportingFiles, ...files];
    const bundleRoot = inferBundleRoot(allFiles.map(rawBundlePathForFile), requiredPaths);
    const merged = new Map(
      supportingFiles.map((file) => [bundlePathForFile(file, requiredPaths, bundleRoot), file])
    );
    files.forEach((file) => merged.set(bundlePathForFile(file, requiredPaths, bundleRoot), file));
    const nextFiles = Array.from(merged.values());
    setSupportingFiles(nextFiles);
    await analyzeUpload(pendingUploadFile, nextFiles);
    e.target.value = '';
  };

  const updateIgnoredPath = async (path: string, shouldIgnore: boolean) => {
    if (!pendingUploadFile) return;
    const nextIgnoredPaths = shouldIgnore
      ? uniqueSortedPaths([...ignoredMissingPaths, path])
      : ignoredMissingPaths.filter((ignoredPath) => ignoredPath !== path);

    setIgnoredMissingPaths(nextIgnoredPaths);
    await analyzeUpload(pendingUploadFile, supportingFiles, nextIgnoredPaths);
  };

  const handleFolderClick = () => {
    if (supportingFolderInputRef.current) {
      supportingFolderInputRef.current.setAttribute('webkitdirectory', '');
      supportingFolderInputRef.current.setAttribute('directory', '');
      supportingFolderInputRef.current.click();
    }
  };

  const submitCompleteBundle = () => {
    if (!pendingUploadFile || !uploadAnalysis?.ready) return;
    uploadMutation.mutate({
      file: pendingUploadFile,
      assets: supportingFiles,
      ignoredPaths: ignoredMissingPaths,
      replaceMicrositeId: pendingUploadTargetId,
    });
  };

  const selectedMicrosite = listQuery.data?.items.find((m) => m.id === selectedId);
  const detail = detailQuery.data;
  const micrositePolicyHasChanges = detail
    ? sharePolicyDraftHasChanges(
        micrositePolicyDraft,
        detail.share_access_mode,
        detail.allowed_email_domains,
        detail.allowed_email_addresses,
      )
    : false;
  const customDomain = customDomainQuery.data ?? null;
  const detailIterations = detail?.iterations ?? [];
  const displayTitle = detail?.title ?? selectedMicrosite?.title ?? '…';
  const isInProgress = detail?.status && IN_PROGRESS_STATUSES.has(detail.status);
  const selectedIteration = detailIterations.find((iteration) => iteration.id === selectedIterationId);
  const isSelectedCurrentIteration =
    selectedIterationId === null || selectedIterationId === detail?.current_iteration_id;
  const selectedPreviewHtml = isSelectedCurrentIteration
    ? detail?.html_content
    : selectedIterationQuery.data?.html_content;
  const canRefineSelectedMicrosite =
    canWrite &&
    detail?.status === 'completed' &&
    Boolean(detail?.html_content) &&
    isSelectedCurrentIteration;
  const canRestoreSelectedIteration =
    canWrite &&
    selectedIteration?.status === 'completed' &&
    selectedIterationId !== null &&
    selectedIterationId !== detail?.current_iteration_id &&
    !isInProgress;
  const canUploadHtmlVersion =
    canWrite &&
    Boolean(detail) &&
    !isInProgress;
  const requiredPathSet = new Set(uploadAnalysis?.required_paths ?? []);
  const ignoredPaths = uploadAnalysis?.ignored_paths ?? ignoredMissingPaths;
  const ignoredPathSet = new Set(ignoredPaths);
  const activeMissingPaths = (uploadAnalysis?.missing_paths ?? []).filter(
    (path) => !ignoredPathSet.has(path)
  );
  const supportingBundleRoot = inferBundleRoot(
    supportingFiles.map(rawBundlePathForFile),
    uploadAnalysis?.required_paths ?? []
  );
  const satisfiedPaths = supportingFiles
    .map((file) => bundlePathForFile(file, uploadAnalysis?.required_paths, supportingBundleRoot))
    .filter((path) => requiredPathSet.has(path))
    .sort();

  const beginTitleEdit = () => {
    setTitleDraft(displayTitle === '…' ? '' : displayTitle);
    setSlugDraft(detail?.slug || suggestSlug(displayTitle === '…' ? '' : displayTitle));
    setIsEditingTitle(true);
  };

  const cancelTitleEdit = () => {
    setTitleDraft('');
    setSlugDraft('');
    setIsEditingTitle(false);
  };

  const submitTitleEdit = () => {
    if (selectedId === null) return;
    const trimmed = titleDraft.trim();
    const slug = (slugDraft.trim() || suggestSlug(trimmed));
    if (!trimmed) {
      addToast({ kind: 'error', message: 'Title cannot be blank' });
      return;
    }
    if (!slug) {
      addToast({ kind: 'error', message: 'Slug cannot be blank' });
      return;
    }
    if (trimmed === displayTitle && slug === detail?.slug) {
      setTitleDraft(displayTitle);
      setSlugDraft(detail?.slug ?? '');
      setIsEditingTitle(false);
      return;
    }
    updateMicrositeMutation.mutate({ id: selectedId, payload: { title: trimmed, slug } });
  };

  const confirmDeleteMicrosite = () => {
    if (selectedId === null) return;
    deleteMutation.mutate(selectedId);
  };

  const restoreSelectedIteration = () => {
    if (selectedId === null || selectedIterationId === null) return;
    activateIterationMutation.mutate({ id: selectedId, iterationId: selectedIterationId });
  };

  const copyText = (value: string, message = 'Copied to clipboard') => {
    navigator.clipboard.writeText(value);
    addToast({ kind: 'success', message });
  };

  const submitCustomDomain = () => {
    if (selectedId === null) return;
    const hostname = domainHostDraft.trim();
    if (!hostname) return;
    createCustomDomainMutation.mutate({ id: selectedId, hostname });
  };

  const closeDeleteConfirm = () => {
    if (deleteMutation.isPending) return;
    setDeleteConfirmOpen(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitTitleEdit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelTitleEdit();
    }
  };

  const sharePathUrl = (path: string) => `${window.location.origin}/share/${path}`;
  const shareFolderUrl = (folder: AssetFolder) => sharePathUrl(folderPath(folder));
  const shareFolderItemUrl = (folder: AssetFolder, itemSlug: string) => `${shareFolderUrl(folder)}/${itemSlug}`;

  const completedMicrosites = listQuery.data?.items.filter((item) => item.status === 'completed') ?? [];
  const selectedFolderItemMicrosite = completedMicrosites.find(
    (microsite) => String(microsite.id) === folderItemDraft.micrositeId
  );
  const shareableFolders = folders.filter((folder) => folder.status !== 'disabled');
  const rootFolders = shareableFolders.filter((folder) => folder.parent_folder_id === null);
  const rootMicrosites = completedMicrosites.filter((microsite) => microsite.parent_folder_id === null);
  const folderDepth = (folder: AssetFolder) => Math.max(0, folderPath(folder).split('/').length - 1);
  const selectedFolderActiveCount = selectedFolder
    ? folders.filter((folder) => folder.parent_folder_id === selectedFolder.id && folder.status !== 'disabled').length
      + completedMicrosites.filter((microsite) => microsite.parent_folder_id === selectedFolder.id).length
      + selectedFolder.items.filter((item) => item.item_type === 'external_link' && item.status !== 'disabled').length
    : 0;
  const selectedFolderChildFolders = selectedFolder
    ? folders.filter((folder) => folder.parent_folder_id === selectedFolder.id && folder.status !== 'disabled')
    : [];
  const selectedFolderMicrosites = selectedFolder
    ? (listQuery.data?.items ?? []).filter(
        (microsite) => microsite.parent_folder_id === selectedFolder.id && microsite.status === 'completed',
      )
    : [];
  const selectedFolderExternalLinks = selectedFolder
    ? selectedFolder.items.filter((item) => item.item_type === 'external_link')
    : [];
  const rootFolderForFolder = (folderId: number | null | undefined) => {
    if (!folderId) return null;
    let current = folders.find((folder) => folder.id === folderId) ?? null;
    let root = current;
    while (current?.parent_folder_id) {
      current = folders.find((folder) => folder.id === current?.parent_folder_id) ?? null;
      if (current) root = current;
    }
    return root;
  };
  const inheritedMicrositePolicyFolder = detail?.parent_folder_id
    ? rootFolderForFolder(detail.parent_folder_id)
    : null;
  const inheritedMicrositePolicyDraft = inheritedMicrositePolicyFolder
    ? policyDraftFromFields(
        inheritedMicrositePolicyFolder.access_mode,
        inheritedMicrositePolicyFolder.allowed_email_domains,
        inheritedMicrositePolicyFolder.allowed_email_addresses,
      )
    : null;

  const submitCreateFolder = () => {
    if (!folderDraft.title.trim()) {
      addToast({ kind: 'error', message: 'Folder title is required' });
      return;
    }
    createFolderMutation.mutate();
  };

  const submitFolderEdit = () => {
    if (!selectedFolder) return;
    if (!folderEditDraft.title.trim()) {
      addToast({ kind: 'error', message: 'Folder title is required' });
      return;
    }
    if (!folderEditDraft.slug.trim()) {
      addToast({ kind: 'error', message: 'Folder slug is required' });
      return;
    }
    updateFolderMutation.mutate({
      folderId: selectedFolder.id,
      payload: {
        title: folderEditDraft.title.trim(),
        slug: folderEditDraft.slug.trim(),
        description: folderEditDraft.description.trim() || null,
        parent_folder_id: folderEditDraft.parentFolderId ? Number(folderEditDraft.parentFolderId) : null,
        ...policyPayloadFromDraft(folderPolicyDraft),
      } as Partial<AssetFolder>,
    });
  };

  const submitMicrositePolicy = () => {
    if (!detail) return;
    updateMicrositeMutation.mutate({
      id: detail.id,
      payload: micrositePolicyPayloadFromDraft(micrositePolicyDraft),
    });
  };

  const submitCreateFolderItem = () => {
    if (!selectedFolderId) return;
    if (folderItemDraft.itemType === 'microsite' && !folderItemDraft.micrositeId) {
      addToast({ kind: 'error', message: 'Choose a completed microsite' });
      return;
    }
    if (folderItemDraft.itemType === 'external_link' && !folderItemDraft.title.trim()) {
      addToast({ kind: 'error', message: 'Enter a link title' });
      return;
    }
    if (folderItemDraft.itemType === 'external_link' && !folderItemDraft.externalUrl.startsWith('https://')) {
      addToast({ kind: 'error', message: 'External links must start with https://' });
      return;
    }
    createFolderItemMutation.mutate();
  };

  const moveFolderItem = (itemId: number, direction: -1 | 1) => {
    if (!selectedFolder) return;
    const activeItems = selectedFolder.items.filter((item) => item.status !== 'disabled');
    const index = activeItems.findIndex((item) => item.id === itemId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= activeItems.length) return;
    const next = [...activeItems];
    [next[index], next[target]] = [next[target], next[index]];
    reorderFolderItemsMutation.mutate({
      folderId: selectedFolder.id,
      itemIds: next.map((item) => item.id),
    });
  };

  return (
    <Layout>
      <Page layout="full-width">
        <PageHeader
          title="Microsite Generator"
          description="Generate UI microsites from a text prompt or upload an HTML file for preview."
          bordered
          actions={
            canWrite ? (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    resetBulkState();
                    setBulkModalOpen(true);
                  }}
                >
                  <ArrowUpTrayIcon className="h-4 w-4" />
                  Bulk Upload
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setActiveArea('microsites');
                    setSelectedFolderId(null);
                    setSelectedId(null);
                    setSelectedIterationId(null);
                    setPromptAttachments([]);
                    setRefinementPrompt('');
                    setIsEditingTitle(false);
                    resetUploadState();
                  }}
                >
                  <PlusIcon className="h-4 w-4" />
                  New Microsite
                </Button>
              </div>
            ) : undefined
          }
        />

        <PageBody fill padded={false}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm"
            className="hidden"
            onChange={handleFileChange}
            data-testid="microsite-html-input"
          />
          <div className="flex flex-1 min-h-0">
            {/* Left panel — history list */}
            <div className="w-64 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface overflow-hidden">
              <div className="flex-shrink-0 p-2 border-b border-gray-200 dark:border-dark-border">
                <div className="grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1 dark:bg-dark-surface-2">
                  <button
                    type="button"
                    onClick={() => setActiveArea('microsites')}
                    className={[
                      'rounded px-2 py-1.5 text-micro font-semibold transition-colors',
                      activeArea === 'microsites'
                        ? 'bg-white text-charcoal shadow-sm dark:bg-dark-surface dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400',
                    ].join(' ')}
                  >
                    History
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveArea('folders')}
                    className={[
                      'rounded px-2 py-1.5 text-micro font-semibold transition-colors',
                      activeArea === 'folders'
                        ? 'bg-white text-charcoal shadow-sm dark:bg-dark-surface dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400',
                    ].join(' ')}
                  >
                    Folders
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto py-1">
                {activeArea === 'microsites' && listQuery.isLoading && (
                  <div className="px-3 pt-2 space-y-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="py-2.5 px-2 space-y-2">
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="45%" height={14} />
                      </div>
                    ))}
                  </div>
                )}

                {activeArea === 'microsites' && !listQuery.isLoading && listQuery.data?.items.length === 0 && (
                  <div className="px-4 py-8 text-center">
                    <PhotoIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-small text-gray-500 dark:text-gray-400">No microsites yet.</p>
                    <p className="text-micro text-gray-400 dark:text-gray-500 mt-0.5">
                      Create one to get started.
                    </p>
                  </div>
                )}

                {activeArea === 'microsites' && listQuery.data?.items.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setSelectedId(m.id);
                      setSelectedIterationId(null);
                      setRefinementPrompt('');
                      setShowPrompt(false);
                      setIsEditingTitle(false);
                      resetUploadState();
                    }}
                    className={[
                      'w-full text-left px-3 py-2.5 rounded-md mx-1 transition-colors',
                      selectedId === m.id
                        ? 'bg-gray-100 dark:bg-dark-surface-2'
                        : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/60',
                    ].join(' ')}
                    style={{ width: 'calc(100% - 8px)' }}
                  >
                    <p className="text-small font-medium text-charcoal dark:text-gray-100 truncate leading-snug">
                      {m.title}
                    </p>
                    <div className="mt-1 flex items-center gap-2 min-w-0">
                      {(IN_PROGRESS_STATUSES.has(m.status) || m.status === 'failed') && (
                        <Badge variant={statusBadgeVariant(m.status)}>{m.status}</Badge>
                      )}
                      <span className="text-micro text-gray-400 dark:text-gray-500 truncate">
                        {formatDate(m.created_at)}
                      </span>
                    </div>
                  </button>
                ))}

                {activeArea === 'folders' && folderListQuery.isLoading && (
                  <div className="px-3 pt-2 space-y-1">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="py-2.5 px-2 space-y-2">
                        <Skeleton variant="text" width="75%" />
                        <Skeleton variant="text" width="50%" height={14} />
                      </div>
                    ))}
                  </div>
                )}

                {activeArea === 'folders' && !folderListQuery.isLoading && folders.length === 0 && rootMicrosites.length === 0 && (
                  <div className="px-4 py-8 text-center">
                    <FolderPlusIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                    <p className="text-small text-gray-500 dark:text-gray-400">No shared items yet.</p>
                  </div>
                )}

                {activeArea === 'folders' && (
                  <div className="px-1 py-1">
                    <button
                      type="button"
                      onClick={() => setSelectedFolderId(null)}
                      className={[
                        'w-full rounded-md px-3 py-2 text-left transition-colors',
                        selectedFolderId === null
                          ? 'bg-gray-100 dark:bg-dark-surface-2'
                          : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/60',
                      ].join(' ')}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <CircleStackIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">Root</p>
                      </div>
                      <p className="mt-1 truncate pl-6 text-micro text-gray-400 dark:text-gray-500">
                        {rootFolders.length + rootMicrosites.length} top-level items
                      </p>
                    </button>

                    {shareableFolders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => setSelectedFolderId(folder.id)}
                        className={[
                          'mt-1 w-full rounded-md py-2 pr-3 text-left transition-colors',
                          selectedFolderId === folder.id
                            ? 'bg-gray-100 dark:bg-dark-surface-2'
                            : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/60',
                        ].join(' ')}
                        style={{ paddingLeft: `${12 + folderDepth(folder) * 16}px` }}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FolderPlusIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">
                            {folder.title}
                          </p>
                        </div>
                        <p className="mt-1 truncate pl-6 text-micro text-gray-400 dark:text-gray-500">
                          /{folderPath(folder)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right panel */}
            <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 dark:bg-dark-bg">
              {activeArea === 'folders' ? (
                <div className="w-full px-6 pt-5 pb-6 space-y-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
                        Share Library
                      </h2>
                      <p className="text-small text-gray-500 dark:text-gray-400">
                        Share clean links for completed microsites and external HTTPS assets.
                      </p>
                    </div>
                    {selectedFolder && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyText(shareFolderUrl(selectedFolder), 'Folder share link copied')}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4 mr-1.5" />
                        Copy share link
                      </Button>
                    )}
                  </div>

                  {folderListQuery.isError && (
                    <Alert variant="error">Share library folders could not be loaded.</Alert>
                  )}

                  {!canWrite && (
                    <Alert variant="info">You can view the share library, but need write access to change it.</Alert>
                  )}

                  {canWrite && (
                    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                      <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                        Create folder
                      </h3>
                      <div className="mt-3 grid gap-3 md:grid-cols-4">
                        <Input
                          value={folderDraft.title}
                          onChange={(e) => setFolderDraft((draft) => ({ ...draft, title: e.target.value }))}
                          placeholder="Folder title"
                        />
                        <Input
                          value={folderDraft.slug}
                          onChange={(e) => setFolderDraft((draft) => ({ ...draft, slug: e.target.value }))}
                          placeholder="optional-slug"
                        />
                        <select
                          value={folderDraft.parentFolderId}
                          onChange={(e) => setFolderDraft((draft) => ({ ...draft, parentFolderId: e.target.value }))}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-small text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                          aria-label="Parent folder"
                        >
                          <option value="">Root</option>
                          {folders
                            .filter((folder) => folder.status !== 'disabled')
                            .map((folder) => (
                              <option key={folder.id} value={folder.id}>
                                {folderPath(folder)}
                              </option>
                            ))}
                        </select>
                        <Button
                          onClick={submitCreateFolder}
                          disabled={createFolderMutation.isPending || !folderDraft.title.trim()}
                        >
                          {createFolderMutation.isPending ? <Spinner size="sm" className="mr-1.5" /> : <FolderPlusIcon className="h-4 w-4 mr-1.5" />}
                          Create
                        </Button>
                      </div>
                      <Textarea
                        value={folderDraft.description}
                        onChange={(e) => setFolderDraft((draft) => ({ ...draft, description: e.target.value }))}
                        placeholder="Optional description"
                        rows={2}
                        className="mt-3"
                      />
                    </div>
                  )}

                  {selectedFolder ? (
                    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
                      <div className="space-y-5">
                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">
                                  {selectedFolder.title}
                                </h3>
                                <Badge variant={selectedFolder.status === 'disabled' ? 'secondary' : 'success'}>
                                  {selectedFolder.status === 'disabled' ? 'Disabled' : 'Shareable'}
                                </Badge>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                <p className="text-small text-gray-500 dark:text-gray-400">
                                  /share/{folderPath(selectedFolder)}
                                </p>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyText(shareFolderUrl(selectedFolder), 'Folder share link copied')}
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4 mr-1.5" />
                                  Copy folder link
                                </Button>
                                {selectedFolder.has_participant_links && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      downloadLinksCsvMutation.mutate({
                                        url: `${API_BASE}/asset-folders/${selectedFolder.id}/participant-links.csv`,
                                        filename: `microsite-links-folder-${selectedFolder.id}.csv`,
                                      })
                                    }
                                    disabled={downloadLinksCsvMutation.isPending}
                                    title="Download a CSV of participant emails and their microsite links"
                                  >
                                    {downloadLinksCsvMutation.isPending ? (
                                      <Spinner size="sm" className="mr-1.5" />
                                    ) : (
                                      <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                    )}
                                    Download links CSV
                                  </Button>
                                )}
                              </div>
                              {selectedFolder.description && (
                                <p className="mt-2 text-small text-gray-500 dark:text-gray-400">
                                  {selectedFolder.description}
                                </p>
                              )}
                              {canWrite && (
                                <div className="mt-4 grid gap-3 md:grid-cols-2">
                                  <Input
                                    value={folderEditDraft.title}
                                    onChange={(e) => setFolderEditDraft((draft) => ({ ...draft, title: e.target.value }))}
                                    placeholder="Folder title"
                                    aria-label="Folder title"
                                  />
                                  <Input
                                    value={folderEditDraft.slug}
                                    onChange={(e) => setFolderEditDraft((draft) => ({ ...draft, slug: e.target.value }))}
                                    placeholder="folder-slug"
                                    aria-label="Folder slug"
                                  />
                                  <select
                                    value={folderEditDraft.parentFolderId}
                                    onChange={(e) => setFolderEditDraft((draft) => ({ ...draft, parentFolderId: e.target.value }))}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-small text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                                    aria-label="Move folder"
                                  >
                                    <option value="">Root</option>
                                    {folders
                                      .filter((folder) => folder.status !== 'disabled' && folder.id !== selectedFolder.id)
                                      .map((folder) => (
                                        <option key={folder.id} value={folder.id}>
                                          {folderPath(folder)}
                                        </option>
                                      ))}
                                  </select>
                                  <Input
                                    value={folderEditDraft.description}
                                    onChange={(e) => setFolderEditDraft((draft) => ({ ...draft, description: e.target.value }))}
                                    placeholder="Optional description"
                                    aria-label="Folder description"
                                  />
                                </div>
                              )}
                            </div>
                            {canWrite && (
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={submitFolderEdit}
                                  disabled={updateFolderMutation.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    const confirmed = window.confirm(
                                      `Delete "${selectedFolder.title}" and everything inside it? This cannot be undone.`
                                    );
                                    if (confirmed) {
                                      deleteFolderMutation.mutate(selectedFolder.id);
                                    }
                                  }}
                                  disabled={deleteFolderMutation.isPending}
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                          <div className="flex items-center justify-between gap-3">
                            <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                              Contents
                            </h3>
                            <span className="text-micro text-gray-400 dark:text-gray-500">
                              {selectedFolderChildFolders.length + selectedFolderMicrosites.length + selectedFolderExternalLinks.filter((item) => item.status !== 'disabled').length} active
                            </span>
                          </div>

                          {selectedFolderChildFolders.length === 0 &&
                          selectedFolderMicrosites.length === 0 &&
                          selectedFolderExternalLinks.length === 0 ? (
                            <div className="mt-4 rounded-lg border border-dashed border-gray-200 p-6 text-center dark:border-dark-border">
                              <DocumentIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
                              <p className="mt-2 text-small text-gray-500 dark:text-gray-400">
                                Move a microsite here, create a child folder, or add an external HTTPS link.
                              </p>
                            </div>
                          ) : (
                            <div className="mt-3 divide-y divide-gray-200 dark:divide-dark-border">
                              {selectedFolderChildFolders.map((folder) => (
                                <div key={`folder-${folder.id}`} className="flex items-center gap-3 py-3">
                                  <FolderPlusIcon className="h-5 w-5 text-gray-400" />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">
                                        {folder.title}
                                      </p>
                                      <Badge variant="success">Folder</Badge>
                                    </div>
                                    <p className="mt-1 truncate text-micro text-gray-400 dark:text-gray-500">
                                      /share/{folderPath(folder)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => copyText(shareFolderUrl(folder), 'Folder share link copied')}
                                      aria-label="Copy folder share link"
                                    >
                                      <ClipboardDocumentIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => window.open(shareFolderUrl(folder), '_blank', 'noopener,noreferrer')}
                                      aria-label="Open folder share page"
                                    >
                                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              {selectedFolderMicrosites.map((microsite) => (
                                <div key={`microsite-${microsite.id}`} className="flex items-center gap-3 py-3">
                                  <DocumentIcon className="h-5 w-5 text-gray-400" />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">
                                        {microsite.title}
                                      </p>
                                      <Badge variant="success">Microsite</Badge>
                                    </div>
                                    <p className="mt-1 truncate text-micro text-gray-400 dark:text-gray-500">
                                      /share/{micrositeSharePath(microsite)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => copyText(sharePathUrl(micrositeSharePath(microsite)), 'Microsite share link copied')}
                                      aria-label="Copy microsite share link"
                                    >
                                      <ClipboardDocumentIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => window.open(sharePathUrl(micrositeSharePath(microsite)), '_blank', 'noopener,noreferrer')}
                                      aria-label="Open microsite share page"
                                    >
                                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedId(microsite.id);
                                        setActiveArea('microsites');
                                      }}
                                    >
                                      Edit
                                    </Button>
                                    {canWrite && (
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() =>
                                          updateMicrositeMutation.mutate({
                                            id: microsite.id,
                                            payload: { parent_folder_id: null },
                                          })
                                        }
                                        aria-label="Move microsite to root"
                                      >
                                        <ArrowPathIcon className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {selectedFolderExternalLinks.map((item) => (
                                <div key={item.id} className="flex items-center gap-3 py-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">
                                        {item.title}
                                      </p>
                                      <Badge variant={item.status === 'disabled' ? 'secondary' : 'success'}>
                                        {item.status === 'disabled' ? 'Disabled' : 'Shareable'}
                                      </Badge>
                                      <span className="text-micro text-gray-400 dark:text-gray-500">
                                        {item.item_type === 'external_link' ? 'external link' : 'microsite'}
                                      </span>
                                    </div>
                                    <p className="mt-1 truncate text-micro text-gray-400 dark:text-gray-500">
                                      {item.item_type === 'external_link' ? item.external_url : `/share/${folderPath(selectedFolder)}/${item.slug}`}
                                    </p>
                                  </div>
                                  {item.status !== 'disabled' && (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() =>
                                          window.open(
                                            item.external_url ?? shareFolderItemUrl(selectedFolder, item.slug),
                                            '_blank',
                                            'noopener,noreferrer',
                                          )
                                        }
                                        aria-label="Open external link"
                                      >
                                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                      </Button>
                                      {canWrite && (
                                        <>
                                          <Button variant="ghost" size="icon-sm" onClick={() => moveFolderItem(item.id, -1)} aria-label="Move item up">
                                            <ChevronUpIcon className="h-4 w-4" />
                                          </Button>
                                          <Button variant="ghost" size="icon-sm" onClick={() => moveFolderItem(item.id, 1)} aria-label="Move item down">
                                            <ChevronDownIcon className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            onClick={() =>
                                              updateFolderMutation.mutate({
                                                folderId: selectedFolder.id,
                                                payload: { homepage_item_id: item.id } as Partial<AssetFolder>,
                                              })
                                            }
                                            aria-label="Set as homepage"
                                          >
                                            <CheckIcon className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            onClick={() => disableFolderItemMutation.mutate({ folderId: selectedFolder.id, itemId: item.id })}
                                            aria-label="Disable item"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-5">
                        {canWrite ? (
                          <FolderAccessControlPanel
                            draft={folderPolicyDraft}
                            setDraft={setFolderPolicyDraft}
                            onSave={submitFolderEdit}
                            isSaving={updateFolderMutation.isPending}
                            hasChanges={folderPolicyHasChanges}
                          />
                        ) : (
                          <AccessPolicySummary draft={folderPolicyDraft} />
                        )}

                        {canWrite && (
                          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                            <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                              Add item
                            </h3>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                              <Button
                                type="button"
                                variant={folderItemDraft.itemType === 'microsite' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFolderItemDraft((draft) => ({ ...draft, itemType: 'microsite' }))}
                              >
                                <DocumentIcon className="h-4 w-4 mr-1.5" />
                                Microsite
                              </Button>
                              <Button
                                type="button"
                                variant={folderItemDraft.itemType === 'external_link' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFolderItemDraft((draft) => ({ ...draft, itemType: 'external_link' }))}
                              >
                                <LinkIcon className="h-4 w-4 mr-1.5" />
                                Link
                              </Button>
                            </div>
                            <div className="mt-3 space-y-3">
                              {folderItemDraft.itemType === 'microsite' ? (
                                <>
                                  <select
                                    value={folderItemDraft.micrositeId}
                                    onChange={(e) => setFolderItemDraft((draft) => ({ ...draft, micrositeId: e.target.value }))}
                                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-small text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                                  >
                                    <option value="">Choose completed microsite</option>
                                    {completedMicrosites.map((microsite) => (
                                      <option key={microsite.id} value={microsite.id}>
                                        {microsite.title}
                                      </option>
                                    ))}
                                  </select>
                                  {selectedFolderItemMicrosite && (
                                    <p className="text-micro text-gray-500 dark:text-gray-400">
                                      Folder item title and slug will use {selectedFolderItemMicrosite.title}.
                                    </p>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Input
                                    value={folderItemDraft.title}
                                    onChange={(e) => setFolderItemDraft((draft) => ({ ...draft, title: e.target.value }))}
                                    placeholder="Link title"
                                  />
                                  <Input
                                    value={folderItemDraft.slug}
                                    onChange={(e) => setFolderItemDraft((draft) => ({ ...draft, slug: e.target.value }))}
                                    placeholder="optional-link-slug"
                                  />
                                  <Input
                                    value={folderItemDraft.externalUrl}
                                    onChange={(e) => setFolderItemDraft((draft) => ({ ...draft, externalUrl: e.target.value }))}
                                    placeholder="https://example.com/deck"
                                  />
                                </>
                              )}
                              <Textarea
                                value={folderItemDraft.description}
                                onChange={(e) => setFolderItemDraft((draft) => ({ ...draft, description: e.target.value }))}
                                placeholder="Optional description"
                                rows={2}
                              />
                              <Button
                                onClick={submitCreateFolderItem}
                                disabled={
                                  createFolderItemMutation.isPending ||
                                  (folderItemDraft.itemType === 'microsite'
                                    ? !folderItemDraft.micrositeId
                                    : !folderItemDraft.title.trim())
                                }
                                className="w-full justify-center"
                              >
                                {createFolderItemMutation.isPending && <Spinner size="sm" className="mr-1.5" />}
                                Add item
                              </Button>
                            </div>
                          </div>
                        )}

                        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                          <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                            Issued magic links
                          </h3>
                          <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
                            These are temporary verification links, not the access allowlist.
                          </p>
                          {canWrite && (
                            <div className="mt-3 flex gap-2">
                              <Input
                                type="email"
                                value={folderAccessEmail}
                                onChange={(e) => setFolderAccessEmail(e.target.value)}
                                placeholder="recipient@example.com"
                              />
                              <Button
                                onClick={() => createFolderAccessLinkMutation.mutate()}
                                disabled={createFolderAccessLinkMutation.isPending || selectedFolder.status === 'disabled'}
                              >
                                Create
                              </Button>
                            </div>
                          )}
                          <div className="mt-3 space-y-2">
                            {folderAccessLinksQuery.isLoading && <Spinner size="sm" />}
                            {folderAccessLinksQuery.data?.items.length === 0 && (
                              <p className="text-small text-gray-500 dark:text-gray-400">No magic links yet.</p>
                            )}
                            {folderAccessLinksQuery.data?.items.map((link) => (
                              <div key={link.id} className="flex items-center justify-between gap-2 rounded-md border border-gray-200 p-2 dark:border-dark-border">
                                <div className="min-w-0">
                                  <p className="truncate text-small text-charcoal dark:text-gray-100">
                                    {link.recipient_email ?? 'Manual link'}
                                  </p>
                                  <p className="text-micro text-gray-400 dark:text-gray-500">
                                    expires {formatDate(link.expires_at)}
                                  </p>
                                </div>
                                {canWrite && !link.revoked_at && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => revokeFolderAccessLinkMutation.mutate({ folderId: selectedFolder.id, linkId: link.id })}
                                  >
                                    Revoke
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
                      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <h3 className="font-subtitle text-h3 text-charcoal dark:text-gray-100">Root</h3>
                            <p className="mt-1 text-small text-gray-500 dark:text-gray-400">
                              Top-level folders and microsites are shareable directly under /share.
                            </p>
                          </div>
                          <Badge variant="secondary">
                            {rootFolders.length + rootMicrosites.length} items
                          </Badge>
                        </div>

                        {rootFolders.length === 0 && rootMicrosites.length === 0 ? (
                          <div className="mt-4 rounded-lg border border-dashed border-gray-200 p-6 text-center dark:border-dark-border">
                            <CircleStackIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
                            <p className="mt-2 text-small text-gray-500 dark:text-gray-400">
                              Create a folder or finish a microsite to populate the root.
                            </p>
                          </div>
                        ) : (
                          <div className="mt-4 divide-y divide-gray-200 dark:divide-dark-border">
                            {rootFolders.map((folder) => (
                              <div key={`root-folder-${folder.id}`} className="flex items-center gap-3 py-3">
                                <FolderPlusIcon className="h-5 w-5 text-gray-400" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">
                                      {folder.title}
                                    </p>
                                    <Badge variant="success">Folder</Badge>
                                  </div>
                                  <p className="mt-1 truncate text-micro text-gray-400 dark:text-gray-500">
                                    /share/{folderPath(folder)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => copyText(shareFolderUrl(folder), 'Folder share link copied')}
                                    aria-label="Copy folder share link"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => window.open(shareFolderUrl(folder), '_blank', 'noopener,noreferrer')}
                                    aria-label="Open folder share page"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedFolderId(folder.id)}
                                  >
                                    Select
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {rootMicrosites.map((microsite) => (
                              <div key={`root-microsite-${microsite.id}`} className="flex items-center gap-3 py-3">
                                <DocumentIcon className="h-5 w-5 text-gray-400" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="truncate text-small font-medium text-charcoal dark:text-gray-100">
                                      {microsite.title}
                                    </p>
                                    <Badge variant="success">Microsite</Badge>
                                  </div>
                                  <p className="mt-1 truncate text-micro text-gray-400 dark:text-gray-500">
                                    /share/{micrositeSharePath(microsite)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => copyText(sharePathUrl(micrositeSharePath(microsite)), 'Microsite share link copied')}
                                    aria-label="Copy microsite share link"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => window.open(sharePathUrl(micrositeSharePath(microsite)), '_blank', 'noopener,noreferrer')}
                                    aria-label="Open microsite share page"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedId(microsite.id);
                                      setActiveArea('microsites');
                                    }}
                                  >
                                    Edit
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                        <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                          Structure
                        </h3>
                        <div className="mt-3 space-y-3 text-small text-gray-500 dark:text-gray-400">
                          <p>Microsites live at root by default and can be moved into folders from their detail toolbar.</p>
                          <p>Folder access grants access to every folder and microsite inside that folder.</p>
                          <p>Deleting a folder removes all nested folders, external links, and microsites in that subtree.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedId === null ? (
                !canWrite ? (
                  <div className="w-full px-6 pt-5 pb-6">
                    <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100 mb-1">
                      Microsite Library
                    </h2>
                    <p className="text-small text-gray-500 dark:text-gray-400">
                      {listQuery.data?.items.length
                        ? 'Select a microsite from history to preview it.'
                        : 'No microsites are available yet.'}
                    </p>
                  </div>
                ) : (
                /* New microsite form */
                <div className="w-full px-6 pt-5 pb-6">
                  <h2 className="font-subtitle text-h3 text-charcoal dark:text-gray-100 mb-1">
                    New Microsite
                  </h2>
                  <p className="text-small text-gray-500 dark:text-gray-400 mb-3">
                    Describe what you want to build and the AI will generate an interactive HTML
                    prototype. You can also upload an existing HTML file.
                  </p>

                  <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex min-w-0 gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
                          <CircleStackIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                              Storage connector
                            </h3>
                            <Badge
                              variant={
                                storageConfigQuery.isLoading
                                  ? 'secondary'
                                  : storageConnected
                                    ? 'success'
                                    : storageRequiredButMissing
                                      ? 'warning'
                                      : 'secondary'
                              }
                            >
                              {storageConfigQuery.isLoading
                                ? 'Checking'
                                : storageConnected
                                  ? 'S3 mirror enabled'
                                  : storageRequiredButMissing
                                    ? 'Required'
                                    : 'Postgres'}
                            </Badge>
                          </div>
                          <p className="mt-1 text-small text-gray-500 dark:text-gray-400">
                            {storageConnected
                              ? 'Microsites are stored in Postgres and mirrored to tenant object storage.'
                              : storageRequiredButMissing
                                ? 'Tenant object storage is required before microsites can be created or updated.'
                                : 'Using Postgres storage. Configure S3 or MinIO to mirror published microsites.'}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                        {storageConnected ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => testStorageMutation.mutate()}
                            disabled={storageBusy}
                          >
                            {testStorageMutation.isPending ? (
                              <Spinner size="sm" className="mr-1.5" />
                            ) : (
                              <ArrowPathIcon className="h-4 w-4" />
                            )}
                            Test connection
                          </Button>
                        ) : (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={refreshStorageConfig}
                              disabled={storageConfigQuery.isLoading}
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              Refresh
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => window.location.assign(STORAGE_CONFIGURE_URL)}
                              disabled={storageConfigQuery.isLoading}
                              data-testid="microsite-storage-configure"
                              data-configure-url={STORAGE_CONFIGURE_URL}
                            >
                              <FolderPlusIcon className="h-4 w-4" />
                              Configure S3 / MinIO
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 border-t border-gray-200 pt-3 text-small dark:border-dark-border sm:grid-cols-3">
                      <div className="min-w-0">
                        <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Provider
                        </p>
                        <p className="mt-1 truncate text-charcoal dark:text-gray-100">
                          {storageConnected
                            ? storageConfigQuery.data?.connector_type?.toUpperCase() || 'S3'
                            : 'Postgres'}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Bucket
                        </p>
                        <p className="mt-1 truncate text-charcoal dark:text-gray-100">
                          {storageConfigQuery.isLoading
                            ? 'Checking...'
                            : storageConnected
                              ? storageConfigQuery.data?.bucket || 'Not configured'
                              : 'Database'}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          Region
                        </p>
                        <p className="mt-1 truncate text-charcoal dark:text-gray-100">
                          {storageConnected ? storageConfigQuery.data?.region || 'Default' : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {storageRequiredButMissing && !storageConfigQuery.isLoading && (
                      <Alert variant="warning" className="mt-4">
                        Tenant object storage is required before creating, uploading, or replacing microsites.
                      </Alert>
                    )}
                  </div>

                  <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4 space-y-3">
                    <Textarea
                      ref={promptTextareaRef}
                      placeholder="Describe the UI you want — e.g. 'A revenue dashboard with a dark sidebar, monthly trend chart, and top-customers table'"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={6}
                      className="min-h-[144px] max-h-[420px] resize-y overflow-y-auto"
                    />
                    {promptAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {promptAttachments.map((attachment, index) => {
                          const key = promptAttachmentKey(attachment, index);
                          const previewUrl = promptAttachmentPreviewUrls[key];
                          return (
                            <div
                              key={key}
                              className="inline-flex max-w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-small text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100"
                            >
                              {previewUrl ? (
                                <img
                                  src={previewUrl}
                                  alt=""
                                  className="h-7 w-7 rounded object-cover"
                                />
                              ) : (
                                <DocumentIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                              )}
                              <span className="max-w-[180px] truncate">{attachment.name}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => removePromptAttachment(index)}
                                disabled={generateMutation.isPending}
                                aria-label={`Remove ${attachment.name}`}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => promptAttachmentsInputRef.current?.click()}
                        disabled={generateMutation.isPending || promptAttachments.length >= 5}
                        aria-label="Attach files to prompt"
                      >
                        <PaperClipIcon className="h-4 w-4 mr-1.5" />
                        Attach
                      </Button>
                      <input
                        ref={promptAttachmentsInputRef}
                        type="file"
                        accept={PROMPT_ATTACHMENT_ACCEPT}
                        multiple
                        className="hidden"
                        onChange={handlePromptAttachmentsChange}
                        data-testid="microsite-prompt-attachments-input"
                      />
                      <Button
                        onClick={handleGenerate}
                        disabled={generateMutation.isPending || !prompt.trim() || !storageAllowsWrites}
                      >
                        {generateMutation.isPending && (
                          <Spinner size="sm" className="mr-1.5" />
                        )}
                        Generate Microsite
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setPendingUploadTargetId(null);
                          fileInputRef.current?.click();
                        }}
                        disabled={uploadMutation.isPending || isAnalyzingUpload || !storageAllowsWrites}
                      >
                        {uploadMutation.isPending || isAnalyzingUpload ? (
                          <Spinner size="sm" className="mr-1.5" />
                        ) : (
                          <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                        )}
                        Upload HTML
                      </Button>
                      <span className="ml-auto text-micro text-gray-400 dark:text-gray-500 hidden sm:block">
                        ⌘↵ to generate
                      </span>
                    </div>

                    {pendingUploadFile && uploadAnalysis && (
                      <div className="rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 p-3 space-y-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <DocumentIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          <span className="text-small font-medium text-charcoal dark:text-gray-100 truncate">
                            {pendingUploadFile.name}
                          </span>
                          {uploadAnalysis.ready ? (
                            <Badge variant="success">ready</Badge>
                          ) : (
                            <Badge variant="warning">missing files</Badge>
                          )}
                        </div>

                        {!uploadAnalysis.ready && (
                          <Alert variant="warning" title="Supporting files needed">
                            Add each local file referenced by this HTML, or ignore references
                            that should resolve to an empty path.
                          </Alert>
                        )}

                        {activeMissingPaths.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Missing
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {activeMissingPaths.map((path) => (
                                <span
                                  key={path}
                                  className="inline-flex max-w-full items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-micro text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300"
                                >
                                  <span className="truncate">{path}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 rounded px-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
                                    onClick={() => updateIgnoredPath(path, true)}
                                    disabled={isAnalyzingUpload || uploadMutation.isPending}
                                    aria-label={`Ignore ${path}`}
                                  >
                                    Ignore
                                  </Button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {ignoredPaths.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Ignored
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {ignoredPaths.map((path) => (
                                <span
                                  key={path}
                                  className="inline-flex max-w-full items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1 text-micro text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300"
                                >
                                  <span className="truncate">{path}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 rounded px-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-dark-surface-2"
                                    onClick={() => updateIgnoredPath(path, false)}
                                    disabled={isAnalyzingUpload || uploadMutation.isPending}
                                    aria-label={`Restore ${path}`}
                                  >
                                    Restore
                                  </Button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {satisfiedPaths.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Added
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {satisfiedPaths.map((path) => (
                                <span
                                  key={path}
                                  className="rounded border border-green-200 bg-green-50 px-2 py-1 text-micro text-green-800 dark:border-green-800/50 dark:bg-green-950/30 dark:text-green-300"
                                >
                                  {path}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {uploadAnalysis.unsupported_references.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Unsupported
                            </p>
                            <div className="space-y-1">
                              {uploadAnalysis.unsupported_references.map((ref) => (
                                <p
                                  key={`${ref.source_path}-${ref.raw_url}`}
                                  className="text-micro text-red-700 dark:text-red-300 break-all"
                                >
                                  {ref.raw_url} ({ref.reason ?? 'unsupported'})
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => supportingFilesInputRef.current?.click()}
                            disabled={isAnalyzingUpload || uploadMutation.isPending}
                          >
                            <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                            Add files
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleFolderClick}
                            disabled={isAnalyzingUpload || uploadMutation.isPending}
                          >
                            <FolderPlusIcon className="h-4 w-4 mr-1.5" />
                            Add folder
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={submitCompleteBundle}
                            disabled={
                              !uploadAnalysis.ready ||
                              isAnalyzingUpload ||
                              uploadMutation.isPending
                            }
                          >
                            {uploadMutation.isPending && <Spinner size="sm" className="mr-1.5" />}
                            {pendingUploadTargetId ? 'Upload HTML version' : 'Upload complete bundle'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={resetUploadState}
                            disabled={uploadMutation.isPending}
                          >
                            Clear
                          </Button>
                        </div>

                        <input
                          ref={supportingFilesInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleSupportingFilesChange}
                          data-testid="microsite-supporting-files-input"
                        />
                        <input
                          ref={supportingFolderInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleSupportingFilesChange}
                          data-testid="microsite-supporting-folder-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
                )
              ) : (
                /* Microsite detail */
                <div className="flex flex-col h-full min-h-full">
                  {/* Detail toolbar */}
                  <div className="flex-shrink-0 bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border px-6 py-4">
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(26rem,42rem)] lg:items-start">
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          {isEditingTitle && detail ? (
                            <div className="flex flex-1 min-w-0 max-w-2xl items-start gap-2">
                              <div className="min-w-0 flex-1 space-y-2">
                                <Input
                                  variant="ghost-lg"
                                  value={titleDraft}
                                  onChange={(e) => setTitleDraft(e.target.value)}
                                  onKeyDown={handleTitleKeyDown}
                                  maxLength={255}
                                  autoFocus
                                  aria-label="Microsite title"
                                  className="min-w-0 font-subtitle text-h3"
                                />
                                <Input
                                  value={slugDraft}
                                  onChange={(e) => setSlugDraft(e.target.value)}
                                  onKeyDown={handleTitleKeyDown}
                                  maxLength={255}
                                  aria-label="Microsite slug"
                                  placeholder="southwest-overview"
                                  className="h-8 text-small"
                                />
                                <p className="text-micro text-gray-400 dark:text-gray-500">
                                  Changing the slug changes the clean share URL.
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={submitTitleEdit}
                                disabled={updateMicrositeMutation.isPending}
                                aria-label="Save title and slug"
                              >
                                {updateMicrositeMutation.isPending ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <CheckIcon className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={cancelTitleEdit}
                                disabled={updateMicrositeMutation.isPending}
                                aria-label="Cancel title edit"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <h2 className="min-w-0 truncate font-subtitle text-h3 text-charcoal dark:text-gray-100">
                                {displayTitle}
                              </h2>
                              {canWrite && detail && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={beginTitleEdit}
                                  aria-label="Edit microsite title"
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                        {detail && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <Badge variant={statusBadgeVariant(detail.status)}>
                              {detail.status}
                            </Badge>
                            {detail.model_used && (
                              <span className="text-micro text-gray-400 dark:text-gray-500">
                                {detail.model_used}
                              </span>
                            )}
                            {detail.completed_at && (
                              <span className="text-micro text-gray-400 dark:text-gray-500">
                                {formatDate(detail.completed_at)}
                              </span>
                            )}
                            {detail.status === 'completed' && (
                              <span className="text-micro text-gray-400 dark:text-gray-500">
                                /share/{micrositeSharePath(detail)}
                              </span>
                            )}
                            {canWrite && (
                              <select
                                value={detail.parent_folder_id ?? ''}
                                onChange={(e) => {
                                  const nextFolderId = e.target.value ? Number(e.target.value) : null;
                                  updateMicrositeMutation.mutate({
                                    id: detail.id,
                                    payload: { parent_folder_id: nextFolderId },
                                  });
                                }}
                                disabled={updateMicrositeMutation.isPending}
                                className="h-8 min-w-0 max-w-full truncate rounded-full border border-gray-300 bg-transparent pl-3 pr-8 text-xs text-charcoal dark:border-dark-border/50 dark:text-gray-200 sm:max-w-64"
                                aria-label="Microsite location"
                              >
                                <option value="">Root</option>
                                {folders
                                  .filter((folder) => folder.status !== 'disabled')
                                  .map((folder) => (
                                    <option key={folder.id} value={folder.id}>
                                      {folderPath(folder)}
                                    </option>
                                  ))}
                              </select>
                            )}
                            {detail.parent_folder_id !== null && !canWrite && folderById.has(detail.parent_folder_id) && (
                              <span className="min-w-0 truncate text-micro text-gray-400 dark:text-gray-500">
                                Folder: /{folderPath(folderById.get(detail.parent_folder_id) ?? null)}
                              </span>
                            )}
                            {detail.parent_folder_id === null && !canWrite && (
                              <span className="text-micro text-gray-400 dark:text-gray-500">
                                Folder: Root
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex min-w-0 flex-col gap-3 lg:items-end">
                        <div className="flex w-full flex-wrap items-center gap-2 lg:justify-end">
                          {detail?.status === 'completed' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  window.open(
                                    sharePathUrl(micrositeSharePath(detail)),
                                    '_blank',
                                    'noopener,noreferrer'
                                  )
                                }
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                                Open
                              </Button>
                              {canWrite && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(sharePathUrl(micrositeSharePath(detail)));
                                    addToast({ kind: 'success', message: 'Clean share link copied' });
                                  }}
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4 mr-1.5" />
                                  Copy share link
                                </Button>
                              )}
                              {canWrite && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => downloadHtmlMutation.mutate(detail)}
                                  disabled={downloadHtmlMutation.isPending}
                                >
                                  {downloadHtmlMutation.isPending ? (
                                    <Spinner size="sm" className="mr-1.5" />
                                  ) : (
                                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                                  )}
                                  Download HTML
                                </Button>
                              )}
                            </>
                          )}
                          {canUploadHtmlVersion && detail && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setPendingUploadTargetId(detail.id);
                                fileInputRef.current?.click();
                              }}
                              disabled={uploadMutation.isPending || isAnalyzingUpload || !storageAllowsWrites}
                            >
                              {uploadMutation.isPending || isAnalyzingUpload ? (
                                <Spinner size="sm" className="mr-1.5" />
                              ) : (
                                <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                              )}
                              {detail.html_content ? 'Re-upload HTML' : 'Upload HTML'}
                            </Button>
                          )}
                          {canWrite && (
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={deleteMutation.isPending}
                              onClick={() => setDeleteConfirmOpen(true)}
                              aria-label="Delete microsite"
                            >
                              {deleteMutation.isPending ? (
                                <Spinner size="sm" />
                              ) : (
                                <TrashIcon className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>

                        {detail?.status === 'completed' && detail.parent_folder_id === null && (
                          <div className="w-full">
                            {canWrite ? (
                              <MicrositeAccessControlPanel
                                draft={micrositePolicyDraft}
                                setDraft={setMicrositePolicyDraft}
                                onSave={submitMicrositePolicy}
                                isSaving={updateMicrositeMutation.isPending}
                                hasChanges={micrositePolicyHasChanges}
                              />
                            ) : (
                              <AccessPolicySummary draft={micrositePolicyDraft} label="Share access" />
                            )}
                          </div>
                        )}
                        {detail?.status === 'completed' && inheritedMicrositePolicyDraft && inheritedMicrositePolicyFolder && (
                          <div className="w-full">
                            <InheritedAccessNotice
                              draft={inheritedMicrositePolicyDraft}
                              sourcePath={folderPath(inheritedMicrositePolicyFolder)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Detail body */}
                  <div className="flex-1 px-6 py-5 overflow-y-auto">
                    {pendingUploadFile && uploadAnalysis && (
                      <div className="mb-4 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2 p-3 space-y-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <DocumentIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          <span className="text-small font-medium text-charcoal dark:text-gray-100 truncate">
                            {pendingUploadFile.name}
                          </span>
                          {uploadAnalysis.ready ? (
                            <Badge variant="success">ready</Badge>
                          ) : (
                            <Badge variant="warning">missing files</Badge>
                          )}
                        </div>

                        {!uploadAnalysis.ready && (
                          <Alert variant="warning" title="Supporting files needed">
                            Add each local file referenced by this HTML, or ignore references
                            that should resolve to an empty path.
                          </Alert>
                        )}

                        {activeMissingPaths.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Missing
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {activeMissingPaths.map((path) => (
                                <span
                                  key={path}
                                  className="inline-flex max-w-full items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-micro text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300"
                                >
                                  <span className="truncate">{path}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 rounded px-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-900/40"
                                    onClick={() => updateIgnoredPath(path, true)}
                                    disabled={isAnalyzingUpload || uploadMutation.isPending}
                                    aria-label={`Ignore ${path}`}
                                  >
                                    Ignore
                                  </Button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {ignoredPaths.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Ignored
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {ignoredPaths.map((path) => (
                                <span
                                  key={path}
                                  className="inline-flex max-w-full items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1 text-micro text-gray-600 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300"
                                >
                                  <span className="truncate">{path}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 rounded px-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-dark-surface-2"
                                    onClick={() => updateIgnoredPath(path, false)}
                                    disabled={isAnalyzingUpload || uploadMutation.isPending}
                                    aria-label={`Restore ${path}`}
                                  >
                                    Restore
                                  </Button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {satisfiedPaths.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Added
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {satisfiedPaths.map((path) => (
                                <span
                                  key={path}
                                  className="rounded border border-green-200 bg-green-50 px-2 py-1 text-micro text-green-800 dark:border-green-800/50 dark:bg-green-950/30 dark:text-green-300"
                                >
                                  {path}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {uploadAnalysis.unsupported_references.length > 0 && (
                          <div>
                            <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1">
                              Unsupported
                            </p>
                            <div className="space-y-1">
                              {uploadAnalysis.unsupported_references.map((ref) => (
                                <p
                                  key={`${ref.source_path}-${ref.raw_url}`}
                                  className="text-micro text-red-700 dark:text-red-300 break-all"
                                >
                                  {ref.raw_url} ({ref.reason ?? 'unsupported'})
                                </p>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => supportingFilesInputRef.current?.click()}
                            disabled={isAnalyzingUpload || uploadMutation.isPending}
                          >
                            <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                            Add files
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleFolderClick}
                            disabled={isAnalyzingUpload || uploadMutation.isPending}
                          >
                            <FolderPlusIcon className="h-4 w-4 mr-1.5" />
                            Add folder
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={submitCompleteBundle}
                            disabled={
                              !uploadAnalysis.ready ||
                              isAnalyzingUpload ||
                              uploadMutation.isPending
                            }
                          >
                            {uploadMutation.isPending && <Spinner size="sm" className="mr-1.5" />}
                            {pendingUploadTargetId ? 'Upload HTML version' : 'Upload complete bundle'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={resetUploadState}
                            disabled={uploadMutation.isPending}
                          >
                            Clear
                          </Button>
                        </div>

                        <input
                          ref={supportingFilesInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleSupportingFilesChange}
                          data-testid="microsite-supporting-files-input"
                        />
                        <input
                          ref={supportingFolderInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleSupportingFilesChange}
                          data-testid="microsite-supporting-folder-input"
                        />
                      </div>
                    )}

                    {!detailQuery.isLoading && detail?.status === 'completed' && (
                      <div className="mb-4 rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-dark-border">
                          <div className="flex min-w-0 items-center gap-2">
                            <LinkIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                            <span className="text-small font-medium text-charcoal dark:text-gray-100">
                              Custom domain
                            </span>
                            {customDomain && (
                              <Badge variant={domainStatusBadgeVariant(customDomain.status)}>
                                {customDomain.status.replace(/_/g, ' ')}
                              </Badge>
                            )}
                          </div>
                          {customDomain && canWrite && customDomain.status !== 'disabled' && (
                            <div className="flex flex-wrap items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => selectedId && checkCustomDomainMutation.mutate(selectedId)}
                                disabled={checkCustomDomainMutation.isPending}
                              >
                                {checkCustomDomainMutation.isPending ? (
                                  <Spinner size="sm" className="mr-1.5" />
                                ) : (
                                  <ArrowPathIcon className="h-4 w-4" />
                                )}
                                Check status
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => selectedId && disableCustomDomainMutation.mutate(selectedId)}
                                disabled={disableCustomDomainMutation.isPending}
                              >
                                {disableCustomDomainMutation.isPending && (
                                  <Spinner size="sm" className="mr-1.5" />
                                )}
                                Disable
                              </Button>
                            </div>
                          )}
                        </div>

                        <div className="p-4 space-y-4">
                          {customDomainQuery.isLoading && (
                            <div className="space-y-2">
                              <Skeleton variant="text" width="40%" />
                              <Skeleton variant="rounded" height={88} />
                            </div>
                          )}

                          {!customDomainQuery.isLoading && !customDomain && canWrite && (
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <Input
                                value={domainHostDraft}
                                onChange={(event) => setDomainHostDraft(event.target.value)}
                                placeholder="customer.elizaplatform.com"
                                aria-label="Custom domain hostname"
                              />
                              <Button
                                type="button"
                                onClick={submitCustomDomain}
                                disabled={createCustomDomainMutation.isPending || !domainHostDraft.trim()}
                                className="sm:flex-shrink-0"
                              >
                                {createCustomDomainMutation.isPending && (
                                  <Spinner size="sm" className="mr-1.5" />
                                )}
                                Add domain
                              </Button>
                            </div>
                          )}

                          {!customDomainQuery.isLoading && !customDomain && !canWrite && (
                            <p className="text-small text-gray-500 dark:text-gray-400">
                              No custom domain is configured.
                            </p>
                          )}

                          {customDomain && (
                            <>
                              <div className="grid gap-3 text-small sm:grid-cols-3">
                                <div className="min-w-0">
                                  <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                    Hostname
                                  </p>
                                  <p className="mt-1 truncate text-charcoal dark:text-gray-100">
                                    {customDomain.hostname}
                                  </p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                    Provider
                                  </p>
                                  <p className="mt-1 truncate text-charcoal dark:text-gray-100">
                                    {customDomain.provider}
                                  </p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-micro font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                    TLS
                                  </p>
                                  <p className="mt-1 truncate text-charcoal dark:text-gray-100">
                                    {customDomain.certificate_status || 'pending'}
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                {[
                                  {
                                    label: 'App ownership TXT',
                                    type: 'TXT',
                                    name: customDomain.ownership_txt_name,
                                    value: customDomain.ownership_txt_value,
                                  },
                                  {
                                    label: 'Routing CNAME',
                                    type: customDomain.routing_record_type,
                                    name: customDomain.routing_record_name,
                                    value: customDomain.routing_record_value,
                                  },
                                  ...(customDomain.provider_validation_records ?? []).map((record) => ({
                                    label: `${record.purpose.replace(/_/g, ' ')} ${record.type}`,
                                    type: record.type,
                                    name: record.name,
                                    value: record.value,
                                  })),
                                ].map((row) => (
                                  <div
                                    key={`${row.label}-${row.name}-${row.value}`}
                                    className="grid gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-small dark:border-dark-border dark:bg-dark-surface-2 md:grid-cols-[160px_80px_1fr_1fr_auto]"
                                  >
                                    <span className="font-medium text-charcoal dark:text-gray-100">
                                      {row.label}
                                    </span>
                                    <span className="font-mono text-micro text-gray-500 dark:text-gray-400">
                                      {row.type}
                                    </span>
                                    <button
                                      type="button"
                                      className="truncate text-left font-mono text-micro text-gray-600 hover:text-charcoal dark:text-gray-300 dark:hover:text-gray-100"
                                      onClick={() => copyText(row.name)}
                                    >
                                      {row.name}
                                    </button>
                                    <button
                                      type="button"
                                      className="truncate text-left font-mono text-micro text-gray-600 hover:text-charcoal dark:text-gray-300 dark:hover:text-gray-100"
                                      onClick={() => copyText(row.value)}
                                    >
                                      {row.value}
                                    </button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => copyText(`${row.type} ${row.name} ${row.value}`, 'DNS row copied')}
                                      aria-label={`Copy ${row.label}`}
                                    >
                                      <ClipboardDocumentIcon className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>

                              {customDomain.last_error && (
                                <Alert variant="warning">
                                  {customDomain.last_error}
                                </Alert>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Version history */}
                    {!detailQuery.isLoading && detailIterations.length > 0 && (
                      <div className="mb-4 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-dark-border">
                          <span className="text-small font-medium text-charcoal dark:text-gray-100">
                            Versions
                          </span>
                          {canRestoreSelectedIteration && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={restoreSelectedIteration}
                              disabled={activateIterationMutation.isPending}
                            >
                              {activateIterationMutation.isPending && (
                                <Spinner size="sm" className="mr-1.5" />
                              )}
                              Restore version
                            </Button>
                          )}
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-dark-border">
                          {detailIterations.map((iteration) => {
                            const isSelected = iteration.id === selectedIterationId;
                            const isCurrent = iteration.id === detail?.current_iteration_id;
                            return (
                              <button
                                key={iteration.id}
                                type="button"
                                onClick={() => setSelectedIterationId(iteration.id)}
                                className={[
                                  'w-full px-4 py-3 text-left transition-colors',
                                  isSelected
                                    ? 'bg-gray-50 dark:bg-dark-surface-2'
                                    : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/60',
                                ].join(' ')}
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-small font-semibold text-charcoal dark:text-gray-100">
                                    Version {iteration.iteration_number}
                                  </span>
                                  {isCurrent && <Badge variant="success">current</Badge>}
                                  <Badge variant={statusBadgeVariant(iteration.status)}>
                                    {iteration.status}
                                  </Badge>
                                  {iteration.model_used && (
                                    <span className="text-micro text-gray-400 dark:text-gray-500">
                                      {iteration.model_used}
                                    </span>
                                  )}
                                  <span className="text-micro text-gray-400 dark:text-gray-500">
                                    {formatDate(iteration.completed_at ?? iteration.created_at)}
                                  </span>
                                </div>
                                <p className="mt-1 line-clamp-2 text-small text-gray-600 dark:text-gray-300">
                                  {iteration.prompt}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {!detailQuery.isLoading && detailIterations.length === 0 && detail?.prompt && (
                      <div className="mb-4 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-surface-2/60 transition-colors"
                          onClick={() => setShowPrompt((v) => !v)}
                        >
                          <span className="text-small font-medium text-charcoal dark:text-gray-100">
                            Prompt
                          </span>
                          {showPrompt
                            ? <ChevronUpIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            : <ChevronDownIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                          }
                        </button>
                        {showPrompt && (
                          <div className="px-4 py-3 border-t border-gray-200 dark:border-dark-border">
                            <p className="text-small text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                              {detail.prompt}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {!detailQuery.isLoading && canRefineSelectedMicrosite && (
                      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">
                            Refine
                          </h3>
                          <span className="hidden text-micro text-gray-400 dark:text-gray-500 sm:block">
                            ⌘↵ to refine
                          </span>
                        </div>
                        <Textarea
                          ref={refinementTextareaRef}
                          placeholder="Ask for changes to this version…"
                          value={refinementPrompt}
                          onChange={(e) => setRefinementPrompt(e.target.value)}
                          onKeyDown={handleRefineKeyDown}
                          rows={3}
                          className="min-h-[88px] max-h-[220px] resize-y overflow-y-auto"
                        />
                        <div className="mt-3 flex justify-end">
                          <Button
                            type="button"
                            onClick={handleRefine}
                            disabled={refineMutation.isPending || !refinementPrompt.trim()}
                          >
                            {refineMutation.isPending && <Spinner size="sm" className="mr-1.5" />}
                            Refine Microsite
                          </Button>
                        </div>
                      </div>
                    )}

                    {detailQuery.isLoading && (
                      <div className="space-y-3 max-w-2xl">
                        <Skeleton variant="text" width="55%" height={18} />
                        <Skeleton variant="rounded" height={480} />
                      </div>
                    )}

                    {!detailQuery.isLoading && isInProgress && (
                      <div className="flex items-center gap-3 py-10">
                        <Spinner size="sm" />
                        <span className="text-small text-gray-500 dark:text-gray-400">
                          Generating your microsite…
                        </span>
                      </div>
                    )}

                    {!detailQuery.isLoading && detail?.status === 'failed' && (
                      <Alert variant="error" className="max-w-2xl">
                        {detail.error_message ?? 'Generation failed. Please try again.'}
                      </Alert>
                    )}

                    {!detailQuery.isLoading && selectedIterationQuery.isLoading && (
                      <div className="flex items-center gap-3 py-10">
                        <Spinner size="sm" />
                        <span className="text-small text-gray-500 dark:text-gray-400">
                          Loading version…
                        </span>
                      </div>
                    )}

                    {!detailQuery.isLoading &&
                      !selectedIterationQuery.isLoading &&
                      detail?.status === 'completed' &&
                      selectedPreviewHtml && (
                        <iframe
                          srcDoc={selectedPreviewHtml}
                          sandbox={PREVIEW_IFRAME_SANDBOX}
                          referrerPolicy="no-referrer"
                          title="Microsite Preview"
                          className="w-full rounded-xl border border-gray-200 dark:border-dark-border bg-white"
                          style={{
                            height: 'calc(100vh - 220px)',
                            minHeight: '400px',
                          }}
                        />
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </PageBody>
      </Page>

      <Modal open={deleteConfirmOpen} onClose={closeDeleteConfirm}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Delete microsite?</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-3">
            <p className="text-small text-gray-600 dark:text-gray-300">
              This will permanently delete{' '}
              <span className="font-medium text-charcoal dark:text-gray-100">
                {displayTitle}
              </span>
              .
            </p>
            <Alert variant="warning">
              This action cannot be undone.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteMicrosite}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Spinner size="sm" className="mr-1.5" />
              ) : (
                <TrashIcon className="h-4 w-4 mr-1.5" />
              )}
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={bulkModalOpen} onClose={closeBulkModal}>
        <ModalContent size="lg" className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden">
          <ModalHeader className="flex-shrink-0">
            <ModalTitle>Bulk upload microsites</ModalTitle>
          </ModalHeader>
          <ModalBody className="min-h-0 flex-1 space-y-4 overflow-y-auto">
            {bulkBatchId == null ? (
              <>
                <p className="text-small text-gray-600 dark:text-gray-300">
                  Upload a <span className="font-medium">.csv</span> or{' '}
                  <span className="font-medium">.xlsx</span> participant export. Each row becomes
                  one microsite, all collected into a single folder and generated in parallel.
                </p>

                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".csv,.xlsx"
                  className="hidden"
                  onChange={handleBulkFileSelect}
                  data-testid="microsite-bulk-input"
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => bulkFileInputRef.current?.click()}
                  >
                    <DocumentIcon className="h-4 w-4 mr-1.5" />
                    {bulkFile ? 'Choose a different file' : 'Choose file'}
                  </Button>
                  {bulkFile && (
                    <span className="text-small text-gray-600 dark:text-gray-300 truncate">
                      {bulkFile.name}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-micro font-semibold text-gray-500 dark:text-gray-400 mb-1">
                    Folder name (optional)
                  </label>
                  <Input
                    value={bulkFolderName}
                    onChange={(e) => setBulkFolderName(e.target.value)}
                    placeholder="Defaults to “Bulk Microsites — <filename>”"
                  />
                </div>

                <div className="rounded-md border border-gray-200 p-3 dark:border-dark-border">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-micro font-semibold uppercase text-gray-500 dark:text-gray-400">
                      Share access
                    </p>
                    <Badge variant="secondary">{accessModeLabel(bulkPolicyDraft.accessMode)}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap items-start gap-2">
                    <AccessScopeSelector
                      value={bulkPolicyDraft.accessMode}
                      onChange={(accessMode) =>
                        setBulkPolicyDraft((current) => ({ ...current, accessMode }))
                      }
                      disabled={bulkCreateMutation.isPending}
                    />
                    <div className="min-w-0 flex-1 basis-72">
                      <AccessPolicyBody
                        draft={bulkPolicyDraft}
                        setDraft={setBulkPolicyDraft}
                        disabled={bulkCreateMutation.isPending}
                      />
                    </div>
                  </div>
                </div>

                {bulkAnalyzing && (
                  <div className="flex items-center gap-2 text-small text-gray-500">
                    <Spinner size="sm" /> Analyzing file…
                  </div>
                )}

                {bulkAnalyzeError && <Alert variant="error">{bulkAnalyzeError}</Alert>}

                {bulkAnalysis && !bulkAnalyzing && (
                  <div className="space-y-3 rounded-md border border-gray-200 dark:border-dark-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="info">{bulkAnalysis.row_count} rows</Badge>
                      {bulkAnalysis.email_column ? (
                        <Badge variant="success">
                          Email: {bulkAnalysis.email_column}
                        </Badge>
                      ) : (
                        <Badge variant="warning">No email column</Badge>
                      )}
                      {bulkAnalysis.ui_prompt_column && (
                        <Badge variant="default">
                          Prompt: {bulkAnalysis.ui_prompt_column}
                        </Badge>
                      )}
                    </div>

                    <div className="text-micro text-gray-500 dark:text-gray-400">
                      Detected columns: {bulkAnalysis.columns.join(', ')}
                    </div>

                    {bulkAnalysis.warnings.map((w, i) => (
                      <Alert key={i} variant="warning">
                        {w}
                      </Alert>
                    ))}

                    <div>
                      <div className="text-micro font-semibold text-gray-500 dark:text-gray-400 mb-1">
                        Sample prompt (first row: {bulkAnalysis.sample_title})
                      </div>
                      <pre className="max-h-48 overflow-auto rounded bg-gray-50 dark:bg-dark-surface-2 p-2 text-micro whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                        {bulkAnalysis.sample_prompt}
                      </pre>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <BulkProgressView batch={bulkBatchQuery.data} isLoading={bulkBatchQuery.isLoading} />
            )}
          </ModalBody>
          <ModalFooter className="flex-shrink-0 border-t border-gray-200 pt-4 dark:border-dark-border">
            {bulkBatchId == null ? (
              <>
                <Button type="button" variant="outline" onClick={closeBulkModal}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => bulkCreateMutation.mutate()}
                  disabled={
                    !bulkFile ||
                    !bulkAnalysis ||
                    bulkPolicyInvalid ||
                    bulkAnalyzing ||
                    bulkCreateMutation.isPending
                  }
                >
                  {bulkCreateMutation.isPending ? (
                    <Spinner size="sm" className="mr-1.5" />
                  ) : (
                    <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                  )}
                  {bulkAnalysis
                    ? `Generate ${bulkAnalysis.row_count} microsites`
                    : 'Generate microsites'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    bulkBatchId != null &&
                    downloadLinksCsvMutation.mutate({
                      url: `${API_BASE}/microsites/bulk/batches/${bulkBatchId}/links.csv`,
                      filename: `microsite-links-batch-${bulkBatchId}.csv`,
                    })
                  }
                  disabled={
                    bulkBatchId == null ||
                    downloadLinksCsvMutation.isPending ||
                    !(
                      bulkBatchQuery.data?.status === 'completed' ||
                      bulkBatchQuery.data?.status === 'completed_with_errors'
                    )
                  }
                  title="Download a CSV of participant emails and their microsite links"
                >
                  {downloadLinksCsvMutation.isPending ? (
                    <Spinner size="sm" className="mr-1.5" />
                  ) : (
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                  )}
                  Download links CSV
                </Button>
                <Button type="button" onClick={closeBulkModal}>
                  Done
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Layout>
  );
}

function BulkProgressView({
  batch,
  isLoading,
}: {
  batch: BulkBatch | undefined;
  isLoading: boolean;
}) {
  if (!batch) {
    return (
      <div className="flex items-center gap-2 text-small text-gray-500">
        <Spinner size="sm" /> {isLoading ? 'Loading batch…' : 'Starting…'}
      </div>
    );
  }
  const counts = batch.counts || {};
  const done = (counts.completed || 0) + (counts.failed || 0);
  const terminal = ['completed', 'completed_with_errors', 'failed'].includes(batch.status);
  const pct = batch.total_count ? Math.round((done / batch.total_count) * 100) : 0;

  const statusBadge = (status: string) => {
    const variant =
      status === 'completed'
        ? 'success'
        : status === 'failed'
          ? 'danger'
          : status === 'generating'
            ? 'info'
            : 'default';
    return <Badge variant={variant as any}>{status}</Badge>;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {!terminal && <Spinner size="sm" />}
        <span className="text-small font-medium text-charcoal dark:text-gray-100">
          {terminal
            ? batch.status === 'failed'
              ? 'Batch failed'
              : batch.status === 'completed_with_errors'
                ? 'Completed with some errors'
                : 'All microsites generated'
            : `Generating… ${done}/${batch.total_count}`}
        </span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded bg-gray-100 dark:bg-dark-surface-2">
        <div
          className="h-full bg-sky-500 transition-all"
          style={{ width: `${pct}%` }}
          data-testid="bulk-progress-bar"
        />
      </div>

      <div className="flex flex-wrap gap-2 text-micro">
        <Badge variant="success">{counts.completed || 0} completed</Badge>
        <Badge variant="info">{counts.generating || 0} generating</Badge>
        <Badge variant="default">
          {(counts.queued || 0) + (counts.pending || 0)} queued
        </Badge>
        {(counts.failed || 0) > 0 && <Badge variant="danger">{counts.failed} failed</Badge>}
      </div>

      {batch.error_message && <Alert variant="error">{batch.error_message}</Alert>}

      <div className="max-h-56 overflow-auto rounded-md border border-gray-200 dark:border-dark-border divide-y divide-gray-100 dark:divide-dark-border">
        {batch.items.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span className="truncate text-small text-gray-700 dark:text-gray-300">
              {item.title}
              {item.participant_email && (
                <span className="text-micro text-gray-400 ml-1">({item.participant_email})</span>
              )}
            </span>
            {statusBadge(item.status)}
          </div>
        ))}
      </div>
    </div>
  );
}
